import { RouteConfig } from 'vue-router';
import { useUserGetters, useUserActions } from '@/store';
import nexusClient from '@/apollo/nexusClient';
import {
  type AuthIsPortfolioCompleteQuery,
  type AuthIsPortfolioCompleteQueryVariables
} from '@/generated/nexus-graphql';

import { IS_PORTFOLIO_COMPLETE_QUERY } from '@/graphql';
import { FEATURE_FLAGS } from '@/constants/featureFlags';

// The typed UserActions helper currently only exposes a subset of actions. Cast to `any` to access
// the additional runtime actions until the store typing is updated.
const { getLatestDocumentByUserId } = (useUserActions as any)(['getLatestDocumentByUserId']);
const { getUserPermissions, clearProgramDoc } = (useUserActions as any)([
  'getUserPermissions',
  'clearProgramDoc'
]);
// Development-only route guard - blocks access in production
const devOnlyGuard = (to: any, from: any, next: any) => {
  if (import.meta.env.DEV || import.meta.env.MODE === 'development') {
    next();
  } else {
    console.warn(`[Router] Attempted to access dev-only route: ${to.path}`);
    next({ path: '/', replace: true });
  }
};

/* CONNECT KILL SWITCH (client side). VITE_EVENT_CONNECT was referenced in
   comments for a year but READ NOWHERE - there was no way to pull the Connect
   surfaces back mid-campaign without a redeploy of something else. Now it is a
   real gate: the switch only fires on the EXPLICIT string 'false', so an unset
   or missing env var can never dark the live app (same fail-open posture as
   the backend allowlist). Flip it in Render, redeploy pcv (auto on master),
   and every Connect surface bounces home. */
const connectKilled = import.meta.env.VITE_EVENT_CONNECT === 'false';
const connectKillGuard = (to: any, from: any, next: any) => {
  if (!connectKilled) {
    next();
    return;
  }
  console.warn(`[Router] Connect is switched off (VITE_EVENT_CONNECT=false): ${to.path}`);
  next({ path: '/', replace: true });
};

const routes: Array<RouteConfig> = [
  // ============================================
  // DEVELOPMENT-ONLY TEST ROUTES
  // These routes are only accessible in dev mode
  // ============================================
  {
    path: '/dev/test-wallet-sponsor',
    name: 'testWalletSponsorCheckout',
    component: () => import('@/views/dev/TestWalletSponsorCheckout.vue'),
    beforeEnter: devOnlyGuard,
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    }
  },
  {
    path: '/dev/time-badge',
    name: 'timeBadgeDemo',
    component: () => import('@/views/dev/TimeBadgeDemo.vue'),
    beforeEnter: devOnlyGuard,
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/journal-test',
    name: 'journalTest',
    component: () => import('@/views/JournalTest.vue'),
    meta: {
      layout: 'default',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/loader',
    name: 'loaderTest',
    component: () => import('@/views/LoaderTest.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    // PilotCity 1,000 Strong — public alumni event (Sat Aug 22, 2026).
    // Anonymous RSVP + $20 ticket. Personalised invite links arrive as ?i=<token>.
    path: '/event/:slug',
    name: 'event1000Strong',
    component: () => import('@/views/Event1000/Event1000Page.vue'),
    // DARK by default (VITE_ALUMNI_REUNION). The OG card is prerendered
    // unconditionally, so the page itself renders a holding state when off
    // rather than silently redirecting a shared link to the homepage.
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity 1,000 Strong'
    }
  },
  {
    // Connect — in-person warm-intro matching (check-in -> swipe -> match ->
    // schedule). DARK behind VITE_EVENT_CONNECT (client) + EVENT_CONNECT
    // (backend). Add ?demo to preview the swipe UI with sample data.
    path: '/event/:slug/connect',
    name: 'event1000Connect',
    beforeEnter: connectKillGuard,
    component: () => import('@/views/Event1000/Connect/ConnectPage.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Connect · 1,000 Strong'
    }
  },
  {
    // Full-screen venue display board (schedule / logos / prep cycle + bell).
    path: '/event/:slug/connect/board',
    name: 'event1000ConnectBoard',
    beforeEnter: connectKillGuard,
    component: () => import('@/views/Event1000/Connect/ConnectBoard.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Connections Board · 1,000 Strong'
    }
  },
  {
    // Staff admin — meetings, spots, remote links, reminders, score inspector.
    path: '/event/:slug/connect/admin',
    name: 'event1000ConnectAdmin',
    beforeEnter: connectKillGuard,
    component: () => import('@/views/Event1000/Connect/ConnectAdmin.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Connect Admin · 1,000 Strong'
    }
  },
  {
    // SMS deep link -> a specific pairing. Declared AFTER board/admin so those
    // exact paths win; the :code catches only real match codes. Same page as
    // /connect; the code is resolved on mount and the pairing opens (OTP if needed).
    path: '/event/:slug/connect/:code',
    name: 'event1000ConnectMatch',
    component: () => import('@/views/Event1000/Connect/ConnectPage.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Connect · 1,000 Strong'
    }
  },
  {
    // Speaker prep — the reference page we send confirmed speakers so they know
    // exactly what to prepare (5-minute lightning talk, no slides). Always on.
    path: '/event/:slug/prep',
    name: 'event1000StrongPrep',
    component: () => import('@/views/Event1000/Event1000PrepPage.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Speaker Prep · 1,000 Strong'
    }
  },
  {
    // Connect home — the "one link" that lists every public event (upcoming as
    // RSVP cards, past as proof the series is real). Top-level /connect, so it
    // never shadows the /event/:slug/connect matching app under /event/.
    // Additive + inherently dormant: nothing links here until it's promoted.
    path: '/connect',
    name: 'connectHome',
    beforeEnter: connectKillGuard,
    component: () => import('@/views/Event1000/ConnectHome.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity Connect'
    }
  },
  /* PROFILE-FIRST + DEEP LINKS: every key surface has a route, so refresh
     keeps your place and any surface is shareable ("go to your doors",
     "this opportunity", "this intro"). All on the standing network tag
     (`networkDeck`), all under the honest /connect/* hierarchy (decision 17b),
     all behind the kill switch, all declared BEFORE /event/:slug so nothing
     shadows. Links POINT - the OTP/session gate still decides what renders.
     NAMING (Derick, 2026-08-31): routes mirror what the user sees. /connect/
     swipe -> /connect/meet (kept as a redirect - it appears in docs only);
     literal /connect/opportunity/:id (the program ObjectId - programmatic, no slug upkeep) over "door"; /connect/portfolio over
     "you" - Constitution Article II names the Portfolio as the atomic owned
     unit, and this profile is increment #1 of that epic. */
  {
    /* The alumni ecosystem's door, the same shape as every other room's
       (/connect/<room>). Same page as /connect; arriving here also means "the
       alumni room", so a phone that last used another ecosystem lands in the
       alumni deck after sign-in. */
    path: '/connect/alumni',
    name: 'connectAlumni',
    beforeEnter: connectKillGuard,
    component: () => import('@/views/Event1000/ConnectHome.vue'),
    meta: { layout: 'no-nav', requiresAuth: false, requiresUser: false, title: 'PilotCity Connect' }
  },
  {
    /* Cognition ecosystem's front door: the alumni landing (ConnectHome) with
       Cognition's content (World/cognitionLanding.ts). Before /connect/w/:slug
       and the /connect/:tab map. */
    path: '/connect/cognition',
    name: 'connectCognition',
    beforeEnter: connectKillGuard,
    component: () => import('@/views/Event1000/ConnectHome.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Cognition Applied AI Talent · PilotCity Connect'
    }
  },
  {
    /* Story 7.7: a world's OWN landing (San Leandro, SCUSD). Numbers from that
       world only; the door carries ?w= so the deck belongs to it. */
    path: '/connect/w/:slug',
    name: 'connectWorld',
    beforeEnter: connectKillGuard,
    component: () => import('@/views/Event1000/World/ConnectWorld.vue'),
    meta: { layout: 'no-nav', requiresAuth: false, requiresUser: false, title: 'PilotCity Connect' }
  },
  { path: '/connect/swipe', redirect: '/connect/meet' },
  // The Pulse sheet's link became the Feed (scope v3, D11); the old link keeps working.
  { path: '/connect/pulse', redirect: '/connect/feed' },
  {
    /* ONE LIVE OPPORTUNITY, PUBLIC AND SHAREABLE. Outside the /connect/*
       session surfaces for the same reason as the two preview routes: it must
       render with no phone, no code and no account, because it is the link you
       paste anywhere. Unsigned on purpose - the card is already live and its id
       ships in every deck payload, so a signature would cost 150 characters and
       protect nothing. The server gates on STATUS: only a live card resolves. */
    path: '/connect/o/:id',
    name: 'connectOpportunityPublic',
    component: () => import('@/views/Event1000/Connect/OpportunityPublic.vue'),
    props: true,
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'An opportunity · PilotCity Connect'
    }
  },
  {
    /* SOMEONE ELSE'S CARDS, PICKED FOR ONE PERSON. Sibling of the giver preview
       above and outside the /connect/* session surfaces for the same reason:
       it renders with no phone, no code and no account, because the people
       worth sending this to have never signed in. The signed token in the path
       is the authorization, and it is scoped to the ids we chose so a share
       never becomes an unlisted catalogue. */
    path: '/connect/for/:token',
    name: 'connectOpportunityShare',
    component: () => import('@/views/Event1000/Connect/OpportunityShare.vue'),
    props: true,
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'People to meet · PilotCity'
    }
  },
  {
    /* A GIVER APPROVING THEIR OWN CARD. Deliberately outside the /connect/*
       session surfaces below: the whole point is that it renders with no phone,
       no code and no account, because the people opening it are alumni who have
       never signed in. The signed token in the path is the authorization, so
       there is nothing here for the kill guard or the OTP gate to decide. */
    path: '/connect/opportunities/preview/:token',
    name: 'connectOpportunityPreview',
    component: () => import('@/views/Event1000/Connect/OpportunityPreview.vue'),
    props: true,
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Your PilotCity opportunity'
    }
  },
  ...(FEATURE_FLAGS.PROGRAM_PREVIEW_LINK
    ? [
        {
          /* A HOST WALKING AN UNPUBLISHED PROGRAM AS A STUDENT WOULD (story PP-3). Same
             shape as the card preview above: the signed token in the path is the whole
             authorization - no session, no OTP, no kill guard. NOT named 'guide': App.vue
             and router/index.ts open the join dialog on that name. OG tags stay generic
             by design (a draft must not unfurl into chat apps - do not "fix"). no-referrer
             because the URL carries the token. Flag off = route absent. */
          path: '/program/preview/:token',
          name: 'programPreview',
          /* PP-8: the walkthrough digest by default; `?view=guide` is the PP-3 Guide. */
          component: () => import('@/views/Guide/ProgramPreviewEntry.vue'),
          props: (route: any) => ({
            previewToken: route.params.token,
            page: Number(route.query.page) || 0
          }),
          meta: {
            layout: 'no-nav',
            requiresAuth: false,
            requiresUser: false,
            noIndex: true,
            title: 'Program preview · PilotCity',
            metaTags: [
              { name: 'referrer', content: 'no-referrer' },
              { name: 'robots', content: 'noindex, nofollow' },
              { property: 'og:title', content: 'PilotCity program preview' }
            ]
          },
          beforeEnter(_to: any, _from: any, next: any) {
            // Never render a preview over a Program doc cached by an earlier session.
            clearProgramDoc();
            next();
          }
        }
      ]
    : []),
  ...(
    [
      ['meet', 'connectMeet', { connectTab: 'deck' }],
      ['1on1s', 'connect1on1s', { connectTab: 'ones' }],
      ['opportunities', 'connectOpportunities', { connectTab: 'opps' }],
      ['portfolio', 'connectPortfolio', { connectTab: 'deck', connectOpen: 'portfolio' }],
      ['feed', 'connectFeed', { connectTab: 'deck', connectOpen: 'feed' }],
      ['opportunity/:id', 'connectOpportunity', { connectTab: 'opps', connectOpen: 'opportunity' }],
      ['intro/:code', 'connectIntro', { connectTab: 'deck', connectOpen: 'intro' }]
    ] as Array<[string, string, Record<string, unknown>]>
  ).map(([path, name, extra]) => ({
    path: `/connect/${path}`,
    name,
    beforeEnter: connectKillGuard,
    component: () => import('@/views/Event1000/Connect/ConnectPage.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      networkDeck: true,
      title: 'PilotCity Connect',
      ...extra
    }
  })),
  /* PLANS (flag VITE_CONNECT_MEMBERSHIP). Free / event seat / membership on
     one screen. Registered ONLY when the flag is the literal 'true' - OFF means
     the route does not exist, so nothing can link a person to a checkout that
     the backend is not ready to honor. Same kill switch as every Connect
     surface; the page itself redirects anon visitors to /connect. */
  ...(import.meta.env.VITE_CONNECT_MEMBERSHIP === 'true'
    ? [
        {
          path: '/connect/plans',
          name: 'connectPlans',
          beforeEnter: connectKillGuard,
          component: () => import('@/views/Event1000/Connect/ConnectPlans.vue'),
          meta: {
            layout: 'no-nav',
            requiresAuth: false,
            requiresUser: false,
            title: 'PilotCity Connect - Plans'
          }
        }
      ]
    : []),
  {
    // The event was briefly promoted as /reunion. "Reunion" was dropped as a
    // framing, but any link already in someone's texts must still land.
    path: '/reunion',
    redirect: '/event/1000strong'
  },
  {
    // Scala-42 — private 2-player card game ("13", House of Eden). Public route:
    // access is room-code + player-token, no account needed for the second
    // player. Dark behind FEATURE_FLAGS.SCALA (page renders a holding state when
    // off so a shared /scala/CODE link never hard-breaks).
    path: '/scala/:roomCode?',
    name: 'scalaGame',
    component: () => import('@/views/Scala/ScalaPage.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: '13 · House of Eden'
    }
  },
  {
    // Public Talent Recruitment tool — browsable alumni directory for recruiters.
    path: '/talent',
    name: 'talentExplorer',
    component: () => import('@/views/Talent/TalentExplorer.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      // Kept in step with the share card in scripts/og-prerender.mjs. The
      // prerendered HTML is what link crawlers read (they do not run JS), so
      // that file is the source of truth; this is the in-app tab title and must
      // not contradict it.
      title: 'PilotCity Talent: browse people by skill, school and company',
      description:
        'The people who came through PilotCity programs - filter by skill, school, company or program, see where they work now, and request a warm intro.',
      metaTags: [
        {
          name: 'description',
          content:
            'The people who came through PilotCity programs - filter by skill, school, company or program, see where they work now, and request a warm intro.'
        },
        {
          property: 'og:title',
          content: 'PilotCity Talent: browse people by skill, school and company'
        },
        {
          property: 'og:image',
          content: 'https://widgets.pilotcity.com/og/talent-v1.png'
        }
      ]
    }
  },
  {
    // Public "where our alumni work" logo wall — credibility + alumni-conference promo.
    path: '/talent/companies',
    name: 'talentCompanies',
    component: () => import('@/views/Talent/TalentCompanies.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity Alumni · Where They Work'
    }
  },
  {
    // Staff-only recruiter-behavior dashboard (data is @pilotcity.com-gated server-side).
    path: '/talent/insights',
    name: 'talentInsights',
    component: () => import('@/views/Talent/TalentInsights.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Talent · Recruiter Insights'
    }
  },
  {
    // Retired 2026-07 — presence folded into the /comms "Presence" panel (Now roster + Trends
    // matrix). Redirect keeps old links working; ?panel=presence auto-opens the panel.
    path: '/presence',
    name: 'presence',
    redirect: { path: '/comms', query: { panel: 'presence' } }
  },
  {
    path: '/network-diagnostics',
    name: 'networkDiagnostics',
    component: () => import('@/views/NetworkDiagnostics.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  // ============================================
  // PEL STRATEGIC LOCATOR
  // Public franchise-location scoring tool
  // (Standalone-branded, PEL yellow + black, no PilotCity nav)
  // ============================================
  {
    path: '/pel-locator',
    name: 'pelLocator',
    component: () => import('@/views/pel-locator/PelLocatorPage.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  // ============================================
  // SEEED PHYSICAL AI SHOWROOM
  // Public digital twin of the 3120 Scott Blvd showroom.
  // Standalone Seeed brand; Tour / Solve / Create + Solutions + Why-Seeed.
  // PilotCity "Showroom Platform" — Seeed is one instance (partner="seeed").
  // ============================================
  {
    path: '/seeed-showroom',
    name: 'seeedShowroom',
    component: () => import('@/views/seeed-showroom/SeeedShowroomPage.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Seeed Physical AI Showroom',
      description:
        'Explore Seeed Studio physical-AI hardware: Perceive → Infer → Act. Tour products, compose solutions, and design your own build.'
    }
  },
  {
    path: '/seeed-showroom/take',
    name: 'seeedShowroomTake',
    component: () => import('@/views/seeed-showroom/SeeedShowroomTake.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Take your Seeed build home'
    }
  },
  {
    path: '/seeed-showroom-admin',
    name: 'seeedShowroomAdmin',
    component: () => import('@/views/seeed-showroom/SeeedShowroomAdmin.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: false
    }
  },
  // ============================================
  // SHIFTSPEND
  // Nurse calculator: how many shifts to afford X
  // Standalone-branded, scrub-blue/teal, mobile-first
  // ============================================
  {
    path: '/shiftspend/login',
    name: 'shiftSpendLogin',
    component: () => import('@/views/ShiftSpend/LoginPage.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/shiftspend',
    name: 'shiftSpendHome',
    component: () => import('@/views/ShiftSpend/HomePage.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true,
      requiresNurse: true
    }
  },
  {
    path: '/shiftspend/new',
    name: 'shiftSpendNew',
    component: () => import('@/views/ShiftSpend/NewEstimatePage.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true,
      requiresNurse: true
    }
  },
  {
    path: '/shiftspend/result/:id',
    name: 'shiftSpendResult',
    component: () => import('@/views/ShiftSpend/ResultPage.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true,
      requiresNurse: true
    }
  },
  {
    path: '/shiftspend/settings',
    name: 'shiftSpendSettings',
    component: () => import('@/views/ShiftSpend/SettingsPage.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true,
      requiresNurse: true
    }
  },
  // ============================================
  // ECOSYSTEM ROUTES
  // Ecosystem Framer — strategic initiative planning
  // ============================================
  {
    path: '/ecosystem',
    name: 'ecosystemList',
    component: () => import('@/views/EcosystemList.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Ecosystems',
      description: 'Explore and create ecosystems'
    }
  },
  {
    path: '/ecosystem/new',
    name: 'ecosystemWizard',
    component: () => import('@/views/EcosystemWizard.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true,
      title: 'Create Ecosystem',
      description: 'Create a new ecosystem'
    }
  },
  {
    path: '/ecosystem/s/:token',
    name: 'ecosystemByToken',
    component: () => import('@/views/EcosystemView.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Ecosystem',
      description: 'Shared ecosystem document'
    }
  },
  {
    // Governance door: the planning/blueprint workspace. Additive route — when
    // ECOSYSTEM_WORLD is off nothing links here, and /ecosystem/:id below still
    // renders the planner directly (byte-for-byte today's behavior).
    path: '/ecosystem/:id/plan',
    name: 'ecosystemPlan',
    component: () => import('@/views/EcosystemView.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true,
      title: 'Ecosystem Blueprint',
      description: 'Ecosystem planning workspace'
    }
  },
  {
    // THE ONE ADMIN HOME (story 6.1). Additive: with GOVERN_HOME off the view
    // renders a "not switched on" note and nothing anywhere links here, so no
    // existing surface changes. Declared BEFORE /ecosystem/:id so the literal
    // segment wins over the param.
    /* Stories 4.3-4.5: the account merge review page. Staff only (the server
       gates on @pilotcity.com); one person decides per pair; nothing deleted. */
    path: '/staff/merge',
    name: 'staffAccountMerge',
    component: () => import('@/views/Staff/AccountMerge.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true,
      title: 'Account merge',
      description: 'Review accounts that are one person'
    }
  },
  {
    path: '/ecosystem/:id/govern',
    name: 'ecosystemGovern',
    component: () => import('@/views/Govern/GovernHome.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true,
      title: 'Govern',
      description: 'The governance home for an ecosystem'
    }
  },
  {
    path: '/ecosystem/:id',
    name: 'ecosystemById',
    // Routing inversion (Ecosystem World): when the flag is ON, entering an
    // ecosystem lands on the living WORLD page; planning moves behind the
    // /plan door above. Flag OFF → the exact same dynamic import of
    // EcosystemView as today (byte-for-byte parity).
    component: () =>
      FEATURE_FLAGS.ECOSYSTEM_WORLD
        ? import('@/views/EcosystemWorld.vue')
        : import('@/views/EcosystemView.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true,
      title: 'Ecosystem',
      description: 'Ecosystem workspace'
    }
  },
  {
    // Ecosystem Portal (C3) — the full-page version of the minibar: the world's
    // square as one screen (rooms nav · thread interleaved with moments · people +
    // downtown). Additive + flag-gated: ECOSYSTEM_PORTAL off → the view immediately
    // redirects home, and nothing anywhere links here.
    path: '/square/:ecosystemId?',
    name: 'ecosystemSquare',
    component: () => import('@/views/EcosystemSquare.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true,
      title: 'The Square',
      description: 'Your world, full screen'
    }
  },
  // Forum → Program-Chat Threads (Program-Modules epic capstone): the standalone
  // /program/:programId/forum route was RETIRED. Per the founder's decision, forum
  // history is now THREADS INSIDE the program community chat — surfaced via a "Threads"
  // affordance in the Program context of ParticipantCommsThread (the minibar), where the
  // thread roots + replies render with the SAME MessageBubble/reaction rail as the chat
  // (reactions inherited for free). The thread-rendering components (ForumThreadsPanel /
  // ForumThreadList / ForumThreadDetail) are reused there; only this route host is gone.
  // The BE thread model + resolvers (programThreads/programThread) are UNCHANGED.
  // ============================================
  // ENDORSEMENT ROUTES (Public)
  // Token-based access for external endorsers
  // ============================================
  {
    path: '/endorse/:token',
    name: 'EndorserDashboard',
    component: () => import('@/views/EndorserDashboard.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  // ============================================
  // REPORT SHARE ROUTES (Public)
  // Token-based access for external report viewers
  // ============================================
  {
    path: '/report/:token',
    name: 'ReportShareAccess',
    component: () => import('@/views/ReportShareAccess.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  // ============================================
  // PARTICIPANT EXPORTS (PARTICIPANT_REPORT_EXPORT)
  // Print-optimized portfolio report + 16:9 deck.
  // No :studentId => the caller's own report; with
  // :studentId => monitor-read variant (BE authz).
  // ============================================
  {
    path: '/program-report/:programId/:studentId?',
    name: 'ProgramReport',
    component: () => import('@/views/ProgramReport.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: true
    }
  },
  {
    path: '/program-deck/:programId/:studentId?',
    name: 'ProgramDeck',
    component: () => import('@/views/ProgramDeck.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: true
    }
  },
  // ============================================
  // SPONSORSHIP ENGINE CLAIM ROUTE (Public)
  // Token-based access for external sponsors to validate
  // credentials + optionally claim a portfolio.
  // ============================================
  {
    path: '/sponsor-claim/:token',
    name: 'SponsorClaim',
    component: () => import('@/views/SponsorClaim.vue'),
    props: true,
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  // ============================================
  // SPONSORSHIP ENGINE TRIGGERS ADMIN (Private)
  // ============================================
  {
    path: '/sponsorship-triggers',
    name: 'SponsorshipTriggers',
    component: () => import('@/views/SponsorshipTriggers.vue'),
    meta: {
      requiresAuth: true,
      requiresUser: true
    }
  },
  {
    path: '/test-activity/start',
    name: 'testCheckoutForm',
    component: () => import('@/views/TestCheckoutForm.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/test-activity/orientation',
    name: 'testOrientation',
    component: () => import('@/views/TestOrientation.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/test-activity/community',
    name: 'testDiscordConnection',
    component: () => import('@/views/TestDiscordConnection.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/test-civics',
    name: 'testCivicsGame',
    component: () => import('@/views/TestCivicsGame.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/test-activity/research',
    name: 'testResourceLinks',
    component: () => import('@/views/TestResourceLinks.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/test-activity/scope',
    name: 'testAcknowledgeBtn',
    component: () => import('@/views/TestAcknowledgeBtn.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/test-activity/participants',
    name: 'testActiveUsersPanel',
    component: () => import('@/views/TestActiveUsersPanel.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/test-activity/peers',
    name: 'testActivityPeers',
    component: () => import('@/views/TestActivityPeers.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/presence-demo',
    name: 'presenceDemo',
    component: () => import('@/views/CachePerformancePlayground.vue'),
    meta: {
      layout: 'dashboard',
      requiresAuth: true,
      requiresUser: true
    }
  },
  {
    path: '/test-sponsor/settings',
    name: 'testSponsorSettings',
    component: () => import('@/views/TestSponsorSettings.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/mock-presentation',
    name: 'mockPresentation',
    component: () => import('@/components/molecules/deliverable/m-presentation.vue'),
    meta: {
      layout: 'no-nav'
    },
    props: {
      value: {
        data: {
          _id: 'mock-program-id',
          program_id: 'mock-program-id',
          adks: [
            {
              name: 'm-presentation',
              fields: [
                {
                  name: 'm-presentation',
                  value: {
                    submittedVideo: {},
                    submittedUrl: '',
                    youtubeOrDriveLink: ''
                  }
                },
                {
                  model: 'videoMaxLength',
                  setup: {
                    videoMaxLength: 10
                  },
                  preset: {
                    videoMaxLength: 10
                  }
                }
              ]
            }
          ]
        }
      },
      adkType: 'm-presentation',
      viewOnly: false,
      isMonitor: false,
      studentId: 'mock-student-id'
    }
  },
  {
    path: '/activity/demo',
    name: 'activityDemo',
    component: () => import('@/components/molecules/deliverable/m-presentation.vue'),
    meta: {
      layout: 'no-nav'
    },
    props: {
      value: {
        data: {
          _id: 'demo-program-id',
          program_id: 'demo-program-id',
          adks: [
            {
              name: 'm-presentation',
              fields: [
                {
                  name: 'm-presentation',
                  value: {
                    submittedVideo: {},
                    submittedUrl: '',
                    youtubeOrDriveLink: ''
                  }
                },
                {
                  model: 'videoMaxLength',
                  setup: {
                    videoMaxLength: 10
                  },
                  preset: {
                    videoMaxLength: 10
                  }
                }
              ]
            }
          ]
        }
      },
      adkType: 'm-presentation',
      viewOnly: false,
      isMonitor: false,
      studentId: 'demo-student-id'
    }
  },
  {
    path: '/temp-setup-program',
    name: 'tempSetupProgram',
    component: () => import('@/views/Guide/components/SetupWizard/SetupProgramWizard.vue'),
    meta: {
      layout: 'no-nav'
    },
    props: {
      website: 'example.com',
      value: {
        program_schema_version: '1.0',
        adks: [
          {
            name: 'Sample ADK',
            description: 'This is a sample ADK',
            type: 'text',
            required: true
          }
        ]
        // Add other fields as needed
      }
    }
  },
  {
    path: '/temp-setup-scopes',
    name: 'tempSetupScopes',
    component: () => import('@/views/Guide/components/SetupWizard/SetupScopesWizard.vue'),
    meta: {
      layout: 'no-nav'
    },
    props: {
      website: 'tesla.com',
      scopes: [
        'Scope 1. This is the first scope description  This is the first scope description  This is the first scope description  This is the first scope description  This is the first scope description  This is the first scope description ',
        'Scope 2 This is the second scope description This is the second scope description This is the second scope description This is the second scope description',
        'Scope 3 This is the third scope description This is the second scope description This is the second scope description This is the second scope description This is the second scope description This is the second scope description This is the second scope description This is the second scope description This is the second scope description This is the second scope description This is the second scope description This is the second scope description This is the second scope description This is the second scope description This is the second scope description'
      ],
      loading: false,
      value: ''
    }
  },
  {
    path: '/mentor',
    name: 'mentor',
    component: () => import('@/views/Mentor.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },

  {
    // RETIRE EVENTUALLY - redirects to /guides/mentorship
    path: '/mentorship',
    redirect: '/guides/mentorship'
  },
  {
    path: '/guides/mentorship',
    name: 'mentorshipGuide',
    component: () => import('@/views/MentorshipGuide.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/match',
    name: 'match',
    component: () => import('@/views/MentorMatch.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/program/mentorship-match',
    name: 'findMatch',
    beforeEnter(to, from, next) {
      window.location.href =
        'https://pilotcity.com/public-program/-pilotcity-mentorship-network-686614734efa2a01fc708d5e';
    }
  },
  {
    path: '/airtable-test',
    name: 'airtableTest',
    component: () => import('@/views/AirtableTest.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/mentee',
    name: 'mentee',
    component: () => import('@/views/Mentee.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/b3diary',
    name: 'b3diary',
    component: () => import('@/views/B3Diary.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/adriane',
    name: 'adriane',
    component: () => import('@/views/Adriane.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/riddhi',
    name: 'riddhi',
    component: () => import('@/views/Riddhi.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/aprajit',
    name: 'aprajit',
    component: () => import('@/views/Aprajit.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/ai-feedback-demo',
    name: 'aiFeedbackDemo',
    component: () => import('@/views/AIFeedbackDemo.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/all-snackbar-messages',
    name: 'allSnackbarMessages',
    component: () => import('@/views/AllSnackbarMessages.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/ai-enhancement-test',
    name: 'aiEnhancementTest',
    component: () => import('@/views/AIEnhancementTest.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/ai-personalization-test',
    name: 'aiPersonalizationTest',
    component: () => import('@/views/AIPersonalizationTest.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/notification-test',
    name: 'notificationTest',
    component: () => import('@/views/NotificationTest.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/audio-test',
    name: 'audioTest',
    component: () => import('@/views/AudioTest.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/leon',
    name: 'leon',
    component: () => import('@/views/Leon.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/activity',
    name: 'ActivityWrapper',
    component: () => import('@/components/activities/activityWrapper.vue'),
    meta: {
      requiresAuth: true,
      layout: 'no-nav'
    }
  },
  {
    path: '/designtime',
    name: 'DesignActivity',
    component: () => import('@/components/activities/m-design/m-design.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/activity/showcase',
    name: 'ShowcaseActivity',
    component: () => import('@/components/activities/m-showcase/m-showcase.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/stage/activity/showcase/test',
    name: 'ShowcaseTest',
    component: () => import('@/views/ShowcaseTest.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/login/:page?',
    name: 'login',
    component: () => import(/* webpackChunkName: "Login" */ '@/views/Login.vue'),
    meta: {
      layout: 'no-nav'
    },
    beforeEnter(to, from, next) {
      const isSponsor: any = localStorage.getItem('isSponsor');
      const selectedOrg: any = localStorage.getItem('SelectedOrg');
      if (JSON.parse(isSponsor)) {
        next({ name: 'sponsor', query: { ...to.query } });
        return;
      }

      if (localStorage.getItem('setRoute') && JSON.parse(selectedOrg)) {
        const toPath = `${localStorage.getItem('setRoute')}?isVerified=true`;
        next({ path: toPath });
        return;
      }
      // if (localStorage.getItem('setRoute')) {
      //   const toPath = `${localStorage.getItem('setRoute')}`;
      //   next({ path: toPath });
      // }
      const { getUser } = useUserGetters(['getUser']);
      if (!getUser.value) {
        // push to landing with popup, preserve redirect param
        next({
          name: 'landing',
          params: { openLoginPopup: 'true' },
          query: { ...to.query }
        });
      } else {
        next({ name: 'portfolio' });
      }
    }
  },
  {
    path: '/signup/:page?',
    name: 'signup',
    component: () => import(/* webpackChunkName: "Signup" */ '@/views/Signup.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    },
    beforeEnter(to, from, next) {
      // const { getUser } = useUserGetters(['getUser']);
      // if (!getUser.value) next();
      // else next({ name: 'portfolio' });
      if (localStorage.getItem('apollo-token')) next({ name: 'portfolio' });
      else next();
    }
  },
  {
    path: '/monitor/:participantId?',
    name: 'Monitor',
    component: () => import(/* webpackChunkName: "Monitor" */ '@/views/Monitor/Monitor.vue'),
    meta: {
      requiresAuth: true,
      layout: 'no-nav'
    }
    // Note: Access control is now data-based. If user has no students to monitor,
    // the Monitor component will show an empty state with guidance.
  },
  {
    path: '/comms',
    name: 'CommsInbox',
    component: () => import(/* webpackChunkName: "CommsInbox" */ '@/views/Comms/CommsInbox.vue'),
    meta: {
      requiresAuth: true,
      layout: 'no-nav'
    }
  },
  {
    path: '/:schemaType/form/:id',
    name: 'AddAdk',
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },
    component: () => import(/* webpackChunkName: "Builder" */ '@/views/form-builder/Builder.vue')
  },
  {
    path: '/:schemaType/form/:id/:page',
    name: 'EditAdK',
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },
    component: () => import(/* webpackChunkName: "Builder" */ '@/views/form-builder/Builder.vue')
  },
  // Legacy Builder.vue route for comparison (temporary)
  {
    path: '/activity-schema-builder2',
    name: 'activitySchemaBuilderLegacy',
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },
    component: () => import(/* webpackChunkName: "Builder" */ '@/views/form-builder/Builder.vue')
  },
  // ActivitySchema builder routes (create/edit mode) - Task Group 7
  {
    path: '/activity-schema-builder',
    name: 'activitySchemaBuilder',
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },
    component: () =>
      import(/* webpackChunkName: "ActivitySchemaEditor" */ '@/views/ActivitySchemaEditor.vue')
  },
  {
    path: '/activity-schema-builder/:id',
    name: 'activitySchemaBuilderEdit',
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },
    component: () =>
      import(/* webpackChunkName: "ActivitySchemaEditor" */ '@/views/ActivitySchemaEditor.vue')
  },
  // Superadmin Activity Analytics - Task: ADK Activity Completion Time Analytics
  {
    path: '/superadmin/activity-analytics',
    name: 'superadminActivityAnalytics',
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },
    component: () =>
      import(
        /* webpackChunkName: "SuperadminActivityAnalytics" */ '@/views/SuperadminActivityAnalytics.vue'
      )
  },
  {
    path: '/program',
    name: 'Monitor:actions',
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/Guide/AirMonitor.vue'),

    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    }
  },
  {
    path: '/program/manage',
    name: 'Manage',
    component: () =>
      import(/* webpackChunkName: "Manage" */ '@/views/ManageProgram/views/Manage.vue')
  },
  {
    path: '/program/participants',
    name: 'Participants',
    component: () =>
      import(/* webpackChunkName: "Participants" */ '@/views/ManageProgram/views/Participants.vue')
  },
  {
    path: '/program/editprogram',
    name: 'Editprogram',
    component: () =>
      import(/* webpackChunkName: "EditProgram" */ '@/views/ManageProgram/views/EditProgram.vue')
  },
  {
    path: '/program/managestakeholder',
    name: 'ManageStakeholder',
    component: () =>
      import(
        /* webpackChunkName: "ManageStakeholder" */ '@/views/ManageProgram/views/ManageStakeholder.vue'
      )
  },
  {
    path: '/program2',
    name: 'ProgramManage',
    component: () =>
      import(/* webpackChunkName: "ProgramManage" */ '@/views/ManageProgram/ProgramManage.vue')
  },
  {
    path: '/ListView',
    name: 'Listview',
    component: () =>
      import(/* webpackChunkName: "ListView" */ '@/views/ManageProgram/components/ListView.vue')
  },
  {
    path: '/nav',
    name: 'Nav',
    component: () =>
      import(/* webpackChunkName: "Nav" */ '@/views/ManageProgram/components/Nav.vue')
  },
  {
    path: '/explore',
    name: 'explore',
    // component: () => import(/* webpackChunkName: "Explore" */ '@/views/Explore/Guide.vue'),
    component: () => import(/* webpackChunkName: "Explore" */ '@/views/Landing.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false // Explore is now available to everyone
    }
    // Note: Removed permission guard - anyone can explore programs
  },
  {
    path: '/scope',
    name: 'scope',
    component: () =>
      import(/* webpackChunkName: "Explore" */ '@/components/molecules/m-build-scope.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    }
  },
  {
    path: '/guide/:programId/:page',
    name: 'guide',
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/Guide/ParticipantGuide.vue'),

    meta: {
      layout: 'no-nav',
      requiresAuth: true
      // requiresUser: true - REMOVED to fix Sentry issue PCV4MAXPRO-23
      // Anonymous users (932 total with anonymous:true flag) are designed for lightweight
      // program participation without portfolio documents. ParticipantGuide.vue doesn't use
      // portfolio data - only needs authentication and basic user info (firstName, lastName, userTypes).
      // This allows users from sponsor links to participate immediately without completing full profile setup.
    },

    props: route => ({
      studentId: route.query.studentId,
      programId: route.params.programId,
      page: Number(route.params.page),
      monitorOrganizerView: route.query.monitorOrganizerView === 'true',
      previewView: route.query.preview === 'true'
    }),

    async beforeEnter(to, from, next) {
      clearProgramDoc();
      next();
    }
  },
  {
    path: '/program/:program_name',
    name: 'program',
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/Guide/ParticipantSetup.vue'),

    meta: {
      layout: 'no-nav',
      requiresAuth: true
      // requiresUser: true - REMOVED (same reason as /guide route - see PCV4MAXPRO-23)
      // ParticipantSetup is part of program participation flow and doesn't require portfolio documents.
    },

    props: route => ({
      studentId: route.query.studentId,
      program_name: route.params.program_name,
      page: Number(route.params.page || 0),
      monitorOrganizerView: route.query.monitorOrganizerView === 'true',
      previewView: route.query.preview === 'true',
      adk: route.query.adk,
      type: 'program'
    })
  },
  {
    path: '/public-program/:program_name',
    name: 'publicProgram',
    component: () => import('@/views/Guide/Setup.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      showBasicUserInfo: true
    },
    props: route => ({
      studentId: route.query.studentId,
      program_name: route.params.program_name,
      page: Number(route.params.page || 0),
      monitorOrganizerView: route.query.monitorOrganizerView === 'true',
      previewView: route.query.preview === 'true',
      adk: route.query.adk,
      type: 'public-program'
    })
  },
  {
    path: '/demo-program/:program_name',
    name: 'demoProgram',
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/Guide/Setup.vue'),

    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    },

    props: route => ({
      studentId: route.query.studentId,
      program_name: route.params.program_name,
      page: Number(route.params.page || 0),
      monitorOrganizerView: route.query.monitorOrganizerView === 'true',
      previewView: route.query.preview === 'true',
      adk: route.query.adk,
      type: 'demo-program'
    })
  },
  {
    path: '/:schemaType/details/:name',
    name: 'Details',
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/Details.vue'),

    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    },

    props: route => ({
      program_name: route.params.name,
      schemaType: route.params.schemaType,
      isTemplate: route.params.isTemplate || route.params.schemaType === 'template'
    })
  },
  {
    path: '/guide',
    name: 'guide:airtime',
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/Guide/AirGuide.vue'),

    meta: {
      layout: 'no-nav',
      requiresAuth: true
      // requiresUser: true - REMOVED (same reason as /guide/:programId/:page - see PCV4MAXPRO-23)
    }
  },
  {
    path: '/guide/:activityName',
    name: 'guide:activity',
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/Guide/ActivityGuide.vue'),

    meta: {
      layout: 'no-nav',
      requiresAuth: true
      // requiresUser: true - REMOVED (same reason as /guide/:programId/:page - see PCV4MAXPRO-23)
    },

    props: route => ({
      activityName: route.params.activityName
    })
  },
  // Portfolio: Declarative redirect for exact path match (fixes NavigationRedirected errors)
  {
    path: '/portfolio',
    redirect: '/portfolio/my-programs',
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    }
  },
  // Family edit-mode detail page (authenticated, Portfolio-scoped)
  // MUST come BEFORE the /portfolio/:component?/:profile? catch-all below
  {
    path: '/portfolio/family/:familyId',
    name: 'familyDetail',
    component: () =>
      import(/* webpackChunkName: "FamilyDetail" */ '@/views/Family/FamilyDetail.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },
    props: true
  },
  // My People Explorer - dedicated page for exploring network connections
  {
    path: '/portfolio/people',
    name: 'my-people-explorer',
    component: () =>
      import(/* webpackChunkName: "MyPeopleExplorer" */ '@/views/Portfolio/MyPeopleExplorer.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    }
  },
  {
    path: '/portfolio/:component?/:profile?',
    name: 'portfolio',
    component: () => import(/* webpackChunkName: "Portfolio" */ '@/views/Portfolio/Portfolio.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },
    props: () => ({
      isPortfolio: true
    }),
    async beforeEnter(to, from, next) {
      // Check for program redirect FIRST to prevent race condition (Fixes PCV4MAXPRO-2G)
      const programRedirect = localStorage.getItem('program');
      if (programRedirect) {
        localStorage.removeItem('program'); // Clean up immediately
        next({ path: `/program/${programRedirect}/0`, replace: true });
        return;
      }

      // Note: Default /portfolio → /portfolio/my-programs handled by declarative redirect above
      // This guard now only handles localStorage business logic redirects

      // Honour any saved route overrides first – global guard already handled auth/user checks.
      const selectedOrg = localStorage.getItem('SelectedOrg');
      const setRoute = localStorage.getItem('setRoute');
      const demoRoute = localStorage.getItem('setDemoProgramRoute');

      if (setRoute && selectedOrg && JSON.parse(selectedOrg)) {
        next({ name: 'wallet' });
        return;
      }

      if (setRoute && setRoute !== to.path) {
        next({ path: setRoute });
        return;
      }

      if (demoRoute && demoRoute !== to.path) {
        localStorage.removeItem('setDemoProgramRoute'); // Clean up to prevent stale redirects
        next({ path: demoRoute, replace: true });
        return;
      }

      next();
    }
  },
  {
    path: '/wallet',
    name: 'wallet',
    component: () => import(/* webpackChunkName: "Portfolio" */ '@/views/Portfolio/Portfolio.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },
    props: () => ({
      isWallet: true
    })
  },
  {
    path: '/payment-wallet',
    name: 'payment-wallet',
    component: () => import(/* webpackChunkName: "Portfolio" */ '@/views/Portfolio/Portfolio.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },
    props: () => ({
      isPaymentWallet: true
    })
  },
  {
    path: '/join/:orgId',
    name: 'pilotcityInvite',
    component: () =>
      import(/* webpackChunkName: "Portfolio" */ '@/views/Sponsor/components/PublicSponsor.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/profile-setting',
    name: 'profileSetting',
    component: () => import(/* webpackChunkName: "Portfolio" */ '@/views/Portfolio/Portfolio.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },
    props: () => ({
      isSetting: true
    })
  },
  {
    path: '/citizen-profile',
    name: 'citizenProfile',
    component: () => import(/* webpackChunkName: "Portfolio" */ '@/views/Portfolio/Portfolio.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },
    props: () => ({
      isCitizen: true
    })
  },
  {
    path: '/payment',
    name: 'payment',
    component: () => import(/* webpackChunkName: "Portfolio" */ '@/views/Portfolio/Portfolio.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },
    props: () => ({
      isPayment: true
    })
  },
  {
    path: '/invite/:slug',
    name: 'invitation',
    component: () =>
      import(/* webpackChunkName: "Invite" */ '@/views/Portfolio/views/GlobalInvitation.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  // Family public share view (view-only, no auth required)
  // See _bmad-output/planning-artifacts/architecture-family.md
  {
    path: '/people/family/tree/:shareToken',
    name: 'familyPublicView',
    component: () =>
      import(/* webpackChunkName: "FamilyPublicView" */ '@/views/Family/FamilyPublicView.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  // (bare /people now serves the People lens page — see the /people route below;
  //  /people/family/tree/:token above is more specific and still matches.)
  {
    path: '/setup',
    name: 'setup',
    component: () => import(/* webpackChunkName: "Setup" */ '@/views/Portfolio/SetupPortfolio.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: true
    },
    beforeEnter(to, from, next) {
      // If this flow includes an explicit redirect query, always allow navigation –
      // the SetupPortfolio component will handle final redirection after profile
      // completion. This prevents the guard from immediately sending new users
      // to the dashboard.
      if (to.query.redirect) {
        next();
        return;
      }

      const { isProfileComplete, getUser } = useUserGetters(['isProfileComplete', 'getUser']);
      const portfolioComplete = isProfileComplete.value === true;
      if (portfolioComplete) {
        if (localStorage.getItem('guardian-consent')) {
          next({
            name: 'sign',
            params: { studentId: `${localStorage.getItem('guardian-consent')}` }
          });
        } else if (localStorage.getItem('sponsorShipLinkPath')) {
          const sponsorShipLinkPath = `${localStorage.getItem('sponsorShipLinkPath')}`;
          next({ path: sponsorShipLinkPath });
        } else if (localStorage.getItem('isAutomationRoute')) {
          next({ name: 'landing' });
        } else if (localStorage.getItem('setRoute')) {
          const toPath = `${localStorage.getItem('setRoute')}`;
          next({ path: toPath });
        } else if (localStorage.getItem('setDemoProgramRoute')) {
          const toPath = `${localStorage.getItem('setDemoProgramRoute')}`;
          next({ path: toPath });
        } else if (getUser.value?._id) {
          // keep existing logic...
          next({ path: '/portfolio/my-programs' });
        }
      } else if (localStorage.getItem('setDemoProgramRoute')) {
        const toPath = `${localStorage.getItem('setDemoProgramRoute')}`;
        next({ path: toPath });
      } else {
        next();
      }
    }
  },
  {
    path: '/setup/:activityName',
    name: 'setup:activity',
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/Guide/AirSetup.vue'),

    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },

    props: route => ({
      activityName: route.params.activityName
    })
  },
  {
    path: '/balanceview',
    name: 'balanceview',
    component: () =>
      import(/* webpackChunkName: "BalanceView" */ '@/views/Portfolio/BalanceView.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    }
  },
  {
    path: '/create',
    name: 'create',
    component: () => import(/* webpackChunkName: "Create" */ '@/views/Create.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    }
  },
  {
    path: '/create-program',
    name: 'createProgram',
    component: () => import(/* webpackChunkName: "CreateProgram" */ '@/views/CreateProgram.vue'),
    meta: {
      requiresAuth: true,
      requiresUser: true
    }
  },
  {
    path: '/ai-program-designer',
    name: 'aiProgramDesigner',
    component: () =>
      import(/* webpackChunkName: "CreateProgram" */ '@/views/AIProgramDesigner.vue'),
    meta: {
      requiresAuth: true
      // requiresUser: true
    }
  },
  {
    path: '/timeline',
    name: 'timeline',
    component: () => import(/* webpackChunkName: "Timeline" */ '@/components/Timeline.vue')
  },
  {
    path: '/sign/:studentId',
    name: 'sign',
    component: () => import(/* webpackChunkName: "Signup" */ '@/views/Agreement.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    },
    beforeEnter(to, from, next) {
      const { getUser } = useUserGetters(['getUser']);
      if (!getUser.value) {
        // Mark this as a guardian-signing flow (downstream signup logic reads these),
        // but DO NOT redirect to login. Agreement.vue (Option A) opens a
        // non-dismissible auth overlay IN PLACE and returns the guardian right back
        // to /sign after auth — so they never get bounced to the landing page.
        localStorage.setItem('guardian-consent', to.params.studentId);
        localStorage.setItem('is-guardian', to.params.studentId);
      }
      next();
    }
  },
  {
    // Build-to-Earn agreement standalone signing page — clone of /sign/:studentId,
    // repointed to StartAgreement.vue (/api/start-agreement). Mirrors the meta/guards
    // exactly. Additive/dark: the page renders a graceful "not available yet" empty
    // state when the flag/gate is off, so reaching it directly never crashes.
    path: '/sign-start/:studentId',
    name: 'sign-start',
    component: () => import(/* webpackChunkName: "Signup" */ '@/views/StartAgreement.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    },
    beforeEnter(to, from, next) {
      const { getUser } = useUserGetters(['getUser']);
      if (!getUser.value) {
        // Mark this as a guardian-signing flow (downstream signup logic reads these),
        // but DO NOT redirect to login. StartAgreement.vue (Option A) opens a
        // non-dismissible auth overlay IN PLACE and returns the guardian right back
        // to /sign-start after auth — so they never get bounced to the landing page.
        localStorage.setItem('guardian-consent', to.params.studentId);
        localStorage.setItem('is-guardian', to.params.studentId);
      }
      next();
    }
  },
  {
    path: '/home',
    name: 'home',
    component: () => import(/* webpackChunkName: "Home" */ '@/views/Home.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/home2',
    name: 'home2',
    component: () => import(/* webpackChunkName: "Home2" */ '@/views/Home2.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — Build Programs That Build People',
      description:
        'PilotCity is the AI-powered platform to create, manage, and measure programs that grow people, organizations, and cities.',
      metaTags: [
        {
          name: 'description',
          content:
            'PilotCity is the AI-powered platform to create, manage, and measure programs that grow people, organizations, and cities.'
        },
        {
          property: 'og:title',
          content: 'PilotCity — Build Programs That Build People'
        },
        {
          property: 'og:description',
          content:
            'Design, launch, and scale programs with AI. Track progress, issue rewards, and measure real-world outcomes.'
        },
        {
          property: 'og:image',
          content: '/og-image-pilotcity.jpg'
        },
        {
          name: 'theme-color',
          content: '#00C897'
        }
      ]
    }
  },
  {
    path: '/home3',
    name: 'home3',
    component: () => import(/* webpackChunkName: "Home3" */ '@/views/Home3.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — Create, Manage & Scale Your Programs With AI',
      description:
        'PilotCity is the AI platform that lets you design programs, automate activities, track participants, and measure impact in one place.',
      metaTags: [
        {
          name: 'description',
          content:
            'PilotCity is the AI platform that lets you design programs, automate activities, track participants, and measure impact in one place.'
        },
        {
          property: 'og:title',
          content: 'PilotCity — Program Management Powered by AI'
        },
        {
          property: 'og:description',
          content:
            "Create programs, track outcomes, and showcase impact — all with PilotCity's AI program builder."
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-software.jpg'
        },
        {
          name: 'theme-color',
          content: '#00C897'
        }
      ]
    }
  },
  {
    path: '/home4',
    name: 'home4',
    component: () => import(/* webpackChunkName: "Home4" */ '@/views/Home4.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — The AI Operating System for Programs',
      description:
        'PilotCity is the AI platform that lets any organization design structured programs, guide participants, and measure outcomes — all in one place.',
      metaTags: [
        {
          name: 'description',
          content:
            'PilotCity is the AI platform that lets any organization design structured programs, guide participants, and measure outcomes — all in one place.'
        },
        {
          property: 'og:title',
          content: 'PilotCity — Build, Run, and Grow Programs'
        },
        {
          property: 'og:description',
          content: "Create, manage, and measure programs in minutes with PilotCity's AI tools."
        },
        {
          property: 'og:image',
          content: '/og-pilotcity.jpg'
        },
        {
          name: 'theme-color',
          content: '#00C897'
        }
      ]
    }
  },
  {
    path: '/home7',
    name: 'home7',
    component: () => import(/* webpackChunkName: "Home7" */ '@/views/Home7.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — Build programs that run themselves',
      description:
        'Build structured programs with AI, guide participants through every step, and measure real outcomes—all in one intelligent platform.',
      metaTags: [
        {
          name: 'description',
          content:
            'Build structured programs with AI, guide participants through every step, and measure real outcomes—all in one intelligent platform.'
        },
        {
          property: 'og:title',
          content: 'PilotCity — Build programs that run themselves'
        },
        {
          property: 'og:description',
          content:
            'The AI operating system for programs. Create, manage, and scale structured experiences that drive real outcomes.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity.jpg'
        },
        {
          name: 'theme-color',
          content: '#00C897'
        }
      ]
    }
  },
  {
    path: '/home7gtm',
    name: 'home7gtm',
    component: () => import(/* webpackChunkName: "Home7GTM" */ '@/views/Home7GTM.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — Build programs that run themselves',
      description:
        'Build structured programs with AI, guide participants through every step, and measure real outcomes—all in one intelligent platform.',
      metaTags: [
        {
          name: 'description',
          content:
            'Build structured programs with AI, guide participants through every step, and measure real outcomes—all in one intelligent platform.'
        },
        {
          property: 'og:title',
          content: 'PilotCity — Build programs that run themselves'
        },
        {
          property: 'og:description',
          content:
            'The AI operating system for programs. Create, manage, and scale structured experiences that drive real outcomes.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity.jpg'
        },
        {
          name: 'theme-color',
          content: '#00C897'
        }
      ]
    }
  },
  {
    path: '/home8',
    name: 'home8',
    component: () => import(/* webpackChunkName: "Home8" */ '@/views/Home8.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity – Build Your City of Innovation',
      description:
        'PilotCity connects students, teachers, employers, and cities to create work-based learning ecosystems that transform communities into engines of innovation.',
      metaTags: [
        {
          name: 'description',
          content:
            'PilotCity connects students, teachers, employers, and cities to create work-based learning ecosystems that transform communities into engines of innovation.'
        },
        {
          property: 'og:title',
          content: 'PilotCity – Build Your City of Innovation'
        },
        {
          property: 'og:description',
          content:
            'Join the platform where schools, employers, and cities collaborate to create opportunity through work-based learning and civic innovation.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity.jpg'
        },
        {
          name: 'theme-color',
          content: '#00C897'
        }
      ]
    }
  },
  {
    path: '/home9',
    name: 'home9',
    component: () => import(/* webpackChunkName: "Home9" */ '@/views/Home9.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity – The Operating System for Human Advancement',
      description:
        'PilotCity lets anyone design, run, and scale programs that create opportunity — for schools, employers, and cities. The OS for human advancement.',
      metaTags: [
        {
          name: 'description',
          content:
            'PilotCity lets anyone design, run, and scale programs that create opportunity — for schools, employers, and cities. The OS for human advancement.'
        },
        {
          property: 'og:title',
          content: 'PilotCity – The Operating System for Human Advancement'
        },
        {
          property: 'og:description',
          content:
            'Design, run, and scale programs that move people and cities forward. PilotCity is the civic operating system for the future of work and learning.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity.jpg'
        },
        {
          name: 'theme-color',
          content: '#00C897'
        }
      ]
    }
  },
  // Retired 2026-10-05: claimed SOC 2, FERPA and Perkins V compliance, verified credentials and
  // automated grant reporting, none of them proven (Master Narrative fix 3).
  { path: '/home10', redirect: '/' },
  {
    path: '/home11',
    name: 'home11',
    component: () => import(/* webpackChunkName: "Home11" */ '@/views/Home11.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — Your New Hometown for Opportunity and Impact',
      description:
        'Join the network of people, places, and programs powering cities into the future. Create, join, and fund programs that drive real-world outcomes.',
      metaTags: [
        {
          name: 'description',
          content:
            'Join the network of people, places, and programs powering cities into the future. Create, join, and fund programs that drive real-world outcomes.'
        },
        {
          name: 'keywords',
          content:
            'work-based learning, civic innovation, AI programs, opportunity network, human programs, digital city'
        },
        {
          property: 'og:title',
          content: 'PilotCity — Your New Hometown for Opportunity and Impact'
        },
        {
          property: 'og:description',
          content:
            'The network of people, places, and programs powering cities into the future. Programs run the world.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity.jpg'
        },
        {
          name: 'theme-color',
          content: '#0052FF'
        }
      ]
    }
  },
  {
    path: '/home12',
    name: 'home12',
    component: () => import(/* webpackChunkName: "Home12" */ '@/views/Home12.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — Your New Hometown for Opportunity',
      description:
        'A network of people, places, and programs transforming how cities grow — where opportunity becomes experience, and experience becomes transformation.',
      metaTags: [
        {
          name: 'description',
          content:
            'A network of people, places, and programs transforming how cities grow — where opportunity becomes experience, and experience becomes transformation.'
        },
        {
          name: 'keywords',
          content:
            'hometown, opportunity network, civic innovation, programs, community development, work-based learning'
        },
        {
          property: 'og:title',
          content: 'PilotCity — Your New Hometown for Opportunity'
        },
        {
          property: 'og:description',
          content:
            'Where lives are transformed. Join the network of programs, people, and places shaping the cities of tomorrow.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-home12.jpg'
        },
        {
          name: 'theme-color',
          content: '#0052FF'
        }
      ]
    }
  },
  {
    path: '/home13',
    name: 'home13',
    component: () => import(/* webpackChunkName: "Home13" */ '@/views/Home13.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — Your New Hometown for Opportunity',
      description:
        'A living network of people, places, and programs where opportunity becomes experience, and experience becomes transformation. Join your city of innovation.',
      metaTags: [
        {
          name: 'description',
          content:
            'A living network of people, places, and programs where opportunity becomes experience, and experience becomes transformation. Join your city of innovation.'
        },
        {
          name: 'keywords',
          content:
            'hometown, opportunity network, civic innovation, programs, community development, work-based learning, cities of innovation, human advancement'
        },
        {
          property: 'og:title',
          content: 'PilotCity — Your New Hometown for Opportunity'
        },
        {
          property: 'og:description',
          content:
            'Where lives are transformed. Join the network of programs, people, and places building cities of innovation.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-home13.jpg'
        },
        {
          name: 'theme-color',
          content: '#0052FF'
        }
      ]
    }
  },
  {
    path: '/home14',
    name: 'home14',
    component: () => import(/* webpackChunkName: "Home14" */ '@/views/Home14.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — Your New Hometown for Opportunity',
      description:
        'Where people, places, and programs connect to create transformation. Born in San Leandro. Built for every city.',
      metaTags: [
        {
          name: 'description',
          content:
            'Where people, places, and programs connect to create transformation. Born in San Leandro. Built for every city.'
        },
        {
          name: 'keywords',
          content:
            'hometown, opportunity network, civic innovation, programs, community development, work-based learning, cities of innovation, human advancement, san leandro'
        },
        {
          property: 'og:title',
          content: 'PilotCity — Your New Hometown for Opportunity'
        },
        {
          property: 'og:description',
          content:
            'Every program connects a person, a place, and a purpose. Join your city of innovation.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-home14.jpg'
        },
        {
          name: 'theme-color',
          content: '#0052FF'
        }
      ]
    }
  },
  {
    path: '/home15',
    name: 'home15',
    component: () => import(/* webpackChunkName: "Home15" */ '@/views/Home15.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — Your New Hometown for Opportunity',
      description:
        'Where people, places, and programs connect to create transformation. Born in San Leandro. Built for every city.',
      metaTags: [
        {
          name: 'description',
          content:
            'Where people, places, and programs connect to create transformation. Born in San Leandro. Built for every city.'
        },
        {
          name: 'keywords',
          content:
            'hometown, opportunity network, civic innovation, programs, community development, work-based learning, cities of innovation, human advancement, san leandro'
        },
        {
          property: 'og:title',
          content: 'PilotCity — Your New Hometown for Opportunity'
        },
        {
          property: 'og:description',
          content:
            'Every program connects a person, a place, and a purpose. Join your city of innovation.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-home15.jpg'
        },
        {
          name: 'theme-color',
          content: '#0052FF'
        }
      ]
    }
  },
  {
    path: '/home18',
    name: 'home18',
    component: () => import(/* webpackChunkName: "Home18" */ '@/views/Home18.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — Your New Hometown for Opportunity',
      description:
        'A living network where people, places, and programs connect to create transformation. Real stories. Real outcomes. Born in San Leandro. Built for every city.'
    }
  },
  {
    // Landing V9 — the recommended hybrid: T1 hero + T2 body.
    // Do something (the program engine) → see who came out of doing it (the
    // talent pool) → understand why it works (the Constitution). Action earns
    // the right to the proof.
    path: '/home26',
    name: 'home26',
    component: () => import(/* webpackChunkName: "Home26" */ '@/views/Home26.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — Describe a program. Meet who finished one.',
      description:
        'Describe a program in a sentence and get a first draft in about a minute. 8,598 people started the same way — they are now at Amazon, Apple, Stanford and NASA.'
    }
  },
  {
    // Participate — the opportunity-seeker landing (Participate v1, 5-P). The
    // live people·places·programs explorer (StudentTabContent → HeroPrograms,
    // the /explore surface) IS the hero, then problem/promise/proof/proposal in
    // the cream/ink system. Nav "Participate" points here (was /explore).
    path: '/participate',
    name: 'participate',
    component: () => import(/* webpackChunkName: "Participate" */ '@/views/Participate.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — Find something real to join.',
      description:
        'Browse real programs, people, and places open right now. Your motivation controls your outcome. 8,598 have joined one.'
    }
  },
  {
    // /people - the public people surface. NO LONGER a LensPage and NO LONGER
    // in the menu: the "Listed under More -> Explore" this comment used to claim
    // stopped being true when the entry was commented out of NavigationBar, and
    // the LensPage part stopped being true with story 9.5. /places is still a
    // LensPage; they are no longer a pair.
    path: '/people',
    name: 'people',
    /* STORY 9.5 - the merge. /people and /directory were two public
       people-browsing surfaces. /people was a marketing frame around
       StudentTabContent; /directory is the real thing, with search, filters and
       the publicProfiles query that HeroPrograms already reuses.
       The surviving URL is /people because it is the one carrying title,
       description and OG meta - /directory had none - and the surviving VIEW is
       the Directory, because it is the one that works. /directory redirects
       here so existing links keep resolving. Both stay out of the nav. */
    component: () => import(/* webpackChunkName: "Directory" */ '@/views/Directory/Directory.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — The people building it',
      description:
        'The citizens of every PilotCity ecosystem — students, alumni, creators, and sponsors. Browse who is here.'
      /* The `lensPage` block that used to live here is gone with the component
         that read it. Only LensPage.vue consumes route.meta.lensPage, and this
         route no longer renders LensPage - leaving the config would be a
         instruction nothing obeys, which is how the next person loses an hour.
         title and description stay: the router still writes those into the page
         meta, and they are why this URL survived the merge. */
    }
  },
  {
    path: '/places',
    name: 'places',
    component: () => import(/* webpackChunkName: "LensPage" */ '@/views/LensPage.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — Where it happens',
      description:
        'The real places hosting PilotCity programs — schools, employers, city halls, and labs. Explore the map.',
      lensPage: {
        eyebrow: 'On the ground',
        title: 'Where it happens.',
        lead: 'The real places hosting programs — schools, employers, city halls, and labs. Explore where the work is happening.',
        lens: 'places'
      }
    }
  },
  {
    // Program Models explorer — a standalone page to browse the ProgramSchema
    // templates ("program models") every program is built from, ranked by real
    // use (programs / participants / completers). Reuses ProgramRankList; data
    // from src/data/programModels.ts (prod snapshot). Public, shareable.
    path: '/program-models',
    name: 'program-models',
    component: () => import(/* webpackChunkName: "ProgramModels" */ '@/views/ProgramModels.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — Program models',
      description:
        'Browse the program models real programs are built on, ranked by how much they have been used. 17 models, 1,341+ programs run.'
    }
  },
  {
    // Landing V13 — the Product → Market → Vision remix (voice memo 2026-07-20).
    // Axis 1 (spine): Product → Market → Vision.
    // Axis 2 (structure of the Product block): Persona · Problem · Promise ·
    //   Proof · Proposal — the five P's, phase-labelled so the framework reads.
    // Axis 3 (audience): written for CREATE (program managers); Participate and
    //   Sponsor are named in the Market, not sold; Vision + Constitution tail.
    // The Promise section is the memo's core: the product AS a process
    //   (create → promote → guide → complete → carry forward). Numbers are the
    //   verified 8,598 / 1,185 (the memo's "1,700" was the retired alumni count).
    path: '/home30',
    name: 'home30',
    component: () => import(/* webpackChunkName: "Home30" */ '@/views/Home30.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — Create a program. Then watch it run.',
      description:
        'For program managers: describe a program and get a running draft in about a minute. 8,598 people have joined one, 1,185 have finished one end to end.'
    }
  },
  {
    // Concept "v2" from the Section Workbench — 9 sections chosen in /sections,
    // reproduced VERBATIM from their source pages in the chosen order.
    // Deliberately un-restyled: five visual systems stacked, so the sequence and
    // the words can be judged without a redesign confounding the comparison.
    // See the docblock in Home29.vue for the claims that must not survive to a
    // public page.
    path: '/home29',
    name: 'home29',
    component: () => import(/* webpackChunkName: "Home29" */ '@/views/Home29.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Concept v2 — internal assembly'
    }
  },
  {
    // Internal tool, not a marketing surface. A workbench for reviewing every
    // section we have ever built across the ~48 landing routes (444 instances,
    // 359 unique after fingerprint dedup) and deciding each one's fate:
    // keep / subpage / discard, plus building and saving named order sequences.
    //
    // Exists because eleven landing versions were judged as finished pages,
    // which only supports a yes/no reaction. The section is the real atomic
    // unit of the decision, and until now no surface showed them together.
    //
    // Data comes from public/section-inventory.json + public/section-shots/,
    // both derived from the RUNNING pages rather than from source parsing, so
    // it reflects what actually renders. Neither is committed — regenerate
    // locally. State persists to localStorage and exports to JSON.
    path: '/sections',
    name: 'section-workbench',
    component: () =>
      import(/* webpackChunkName: "SectionWorkbench" */ '@/views/SectionWorkbench.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Section Workbench — internal'
    }
  },
  {
    // Landing V11 — service-led, written for the person who runs the program.
    // The first version to name a pain before promising anything: /home19-20
    // did that and every version after dropped it. Sells the service we
    // actually deliver (retainer) rather than self-serve software, and every
    // scale number on it is from defensible-claims.md.
    path: '/home28',
    name: 'home28',
    component: () => import(/* webpackChunkName: "Home28" */ '@/views/Home28.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — You bring the intent. We run the program.',
      description:
        'Programs designed, run, and reported for you. 8,598 people have joined one and 1,185 have finished one end to end. Start with a single term.'
    }
  },
  {
    // Landing V10 — "The Classroom → the Cohort". A full redesign of the visual
    // system rather than the argument: the same verified numbers and people as
    // /home26, rebuilt on cream/ink plus the product's real seven-colour
    // spectrum, where colour means identity (68 of ~70 ADKs already pick their
    // hue from that same array). /home26 stays untouched as the control.
    path: '/home27',
    name: 'home27',
    component: () => import(/* webpackChunkName: "Home27" */ '@/views/Home27.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — A hundred students a term. Here is where they went.',
      description:
        'Every city has the same raw asset: its youngest citizens, sitting in classrooms. 8,598 have joined a program, 1,185 have finished one, and they are now at Apple, Google, OpenAI and Anduril.'
    }
  },
  {
    // Landing V8 — T2, "The Talent Pool". Ranked first of twelve theses
    // (19/20) in landing-thesis-catalog.md and never attempted: highest
    // demand, fully provable, and impossible to copy — you cannot fake a
    // decade of people.
    path: '/home25',
    name: 'home25',
    component: () => import(/* webpackChunkName: "Home25" */ '@/views/Home25.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — 8,598 young people have built real work',
      description:
        'They started in classrooms on projects for real employers. They are now engineers at Amazon and Apple, researchers at Stanford and NASA, and founders of their own companies.'
    }
  },
  {
    // Landing V7 — the A→Z framework.
    //   A    the product  — a grounded action a stranger can take immediately
    //   A→Z  the leap     — one sentence tying that action to the largest reason
    //   Z    the vision   — why we exist (the Constitution)
    //   B→Y  the markets  — the product-market-fit loops that carry A to Z
    path: '/home24',
    name: 'home24',
    component: () => import(/* webpackChunkName: "Home24" */ '@/views/Home24.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — Start with one program',
      description:
        'A program, repeated, becomes a behavior. Behaviors, aggregated, become a culture. Describe a program in a sentence and start there.'
    }
  },
  {
    // Landing V6 — claims-corrected. /home22's spine with every figure and
    // capability re-verified against production 2026-07-19. The prior set
    // (19 ecosystems / 1,772 alumni / 2,449 programs / 15,772 initiatives) was
    // retired after an adversarial audit; see defensible-claims.md.
    path: '/home23',
    name: 'home23',
    component: () => import(/* webpackChunkName: "Home23" */ '@/views/Home23.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — Describe a program. Watch it get built.',
      description:
        '8,598 people have joined a program on PilotCity and 1,185 have finished one. Describe a program in a sentence and get a first draft in about a minute.'
    }
  },
  {
    // Landing V5 — epiphany-first. /home21's spine, but the hero is the
    // interactive prompt box restored from the production hero: describe a
    // program, watch it get built. Same /create + auth handoff.
    path: '/home22',
    name: 'home22',
    component: () => import(/* webpackChunkName: "Home22" */ '@/views/Home22.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — Describe a program. Watch it get built.',
      description:
        'Programs are how a community turns its own people into its economic, workforce, and cultural engine. Describe one in a sentence and watch it get built.'
    }
  },
  {
    // The public Constitution. Adapted from Nexus/docs/CONSTITUTION.md — the
    // philosophy and the verified grounding only. Implementation status, schema,
    // the business-model ladder, and the roadmap stay internal.
    path: '/constitution',
    name: 'constitution',
    component: () => import(/* webpackChunkName: "Constitution" */ '@/views/Constitution.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'The PilotCity Constitution',
      description:
        'The canonical statement of what PilotCity is and why it is built this way. Actions become behavior; behavior becomes culture. Ratified 2026, verified against production.'
    }
  },
  {
    // /constitution-v2 — the Constitution recomposed as the company's public
    // landing page (the full document stays at /constitution).
    path: '/constitution-v2',
    name: 'constitution-v2',
    component: () => import(/* webpackChunkName: "ConstitutionV2" */ '@/views/ConstitutionV2.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — every community, an incubator of innovation',
      description:
        'What PilotCity is, at a glance: turn every community into an incubator of innovation, powered by its own people. The mission, the flywheel, the theory of change, and the proof.'
    }
  },
  {
    // Landing V4 — thesis-first. Same cream language and evidence bands as
    // /home20, different spine: opens on the mission (Constitution preamble)
    // rather than the problem, because what's being sold is the thesis
    // demonstrated — the tools are how it gets delivered.
    path: '/home21',
    name: 'home21',
    component: () => import(/* webpackChunkName: "Home21" */ '@/views/Home21.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — Every community is an incubator of innovation',
      description:
        "We design, run, and measure the programs that turn a community's own people into its economic, workforce, and cultural engine — one program, one initiative, one ecosystem at a time."
    }
  },
  {
    // Landing V3 — narrative consolidation. Same argument as /home19 in 7
    // sections instead of 14; purpose-built rather than forked.
    path: '/home20',
    name: 'home20',
    component: () => import(/* webpackChunkName: "Home20" */ '@/views/Home20.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — Stop running programs manually',
      description:
        'Your fractional Chief Program Officer. We design, build, and run the programs that turn effort into outcomes — opportunities for participants, outcomes for creators and sponsors.'
    }
  },
  {
    // Landing V2 preview. Runs side-by-side with /home18 (the control) so the
    // two can be compared live before any cutover of the root `/` route.
    path: '/home19',
    name: 'home19',
    component: () => import(/* webpackChunkName: "Home19" */ '@/views/Home19.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — Stop running programs manually',
      description:
        'Your fractional Chief Program Officer. We design, build, and run the programs that turn effort into outcomes — opportunities for participants, outcomes for creators and sponsors.'
    }
  },
  {
    path: '/home16',
    name: 'home16',
    component: () => import(/* webpackChunkName: "Home16" */ '@/views/Home16.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — Your New Hometown for Opportunity',
      description:
        'A living network where people, places, and programs connect to create transformation. Real stories. Real outcomes. Born in San Leandro. Built for every city.',
      metaTags: [
        {
          name: 'description',
          content:
            'A living network where people, places, and programs connect to create transformation. Real stories. Real outcomes. Born in San Leandro. Built for every city.'
        },
        {
          name: 'keywords',
          content:
            'hometown, opportunity network, civic innovation, programs, community development, work-based learning, cities of innovation, human advancement, san leandro, testimonials, transformation stories'
        },
        {
          property: 'og:title',
          content: 'PilotCity — Your New Hometown for Opportunity'
        },
        {
          property: 'og:description',
          content:
            'See the network in action. Real people, real places, real programs creating real transformation.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-home16.jpg'
        },
        {
          name: 'theme-color',
          content: '#0052FF'
        }
      ]
    }
  },
  {
    path: '/home17',
    name: 'home17',
    component: () => import(/* webpackChunkName: "Home17" */ '@/views/Home17.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity — 您的新家乡为了机会',
      description:
        '一个人、地点和项目连接以创造转变的活力网络。真实故事。真实成果。诞生于圣利安卓。为每个城市而建。',
      metaTags: [
        {
          name: 'description',
          content:
            '一个人、地点和项目连接以创造转变的活力网络。真实故事。真实成果。诞生于圣利安卓。为每个城市而建。'
        },
        {
          name: 'keywords',
          content:
            '家乡, 机会网络, 公民创新, 项目, 社区发展, 基于工作的学习, 创新城市, 人类进步, 圣利安卓, 推荐, 转型故事'
        },
        {
          property: 'og:title',
          content: 'PilotCity — 您的新家乡为了机会'
        },
        {
          property: 'og:description',
          content: '见证网络的作用。真实的人、真实的地点、真实的项目创造真实的转变。'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-home16.jpg'
        },
        {
          name: 'theme-color',
          content: '#0052FF'
        }
      ]
    }
  },
  {
    path: '/for-students',
    name: 'for-students',
    component: () => import(/* webpackChunkName: "ForStudents" */ '@/views/ForStudents.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity for Students — Your First Real Opportunity Starts Here',
      description:
        'Get real internships, build your portfolio, earn credentials. Join programs that transform your future. 10,000+ students already building their path.',
      metaTags: [
        {
          name: 'description',
          content:
            'Get real internships, build your portfolio, earn credentials. Join programs that transform your future. 10,000+ students already building their path.'
        },
        {
          name: 'keywords',
          content:
            'student internships, high school opportunities, work experience, portfolio building, college applications, career development, real-world projects, earn credentials, student programs, youth opportunities'
        },
        {
          property: 'og:title',
          content: 'PilotCity for Students — Real Opportunities, Real Experience'
        },
        {
          property: 'og:description',
          content:
            'Complete real projects, earn credentials, build your portfolio. Step-by-step guidance. Community support. Free to start.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-students.jpg'
        },
        {
          name: 'theme-color',
          content: '#06B6D4'
        }
      ]
    }
  },
  {
    // Vertical pages from the thesis catalog. Deliberately NOT at
    // /for-employers — that route already exists and serves a different page.
    //   /employers-v2  T2 talent-led (catalog rank 1)
    //   /districts-v2  T3 work-based learning (catalog rank 4)
    path: '/employers-v2',
    name: 'employersV2',
    component: () => import(/* webpackChunkName: "EmployersV2" */ '@/views/EmployersV2.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity for employers — meet engineers before everyone else does',
      description:
        '8,598 young people have built real projects for real companies. Hire from the pool, sponsor a cohort, or host a program on-site.'
    }
  },
  {
    path: '/districts-v2',
    name: 'districtsV2',
    component: () => import(/* webpackChunkName: "ForDistricts" */ '@/views/ForDistricts.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity for districts — work-based learning, actually delivered',
      description:
        'Your students build real projects for real employers and finish with something to show. Start with one teacher and one term.'
    }
  },
  {
    path: '/for-employers',
    name: 'for-employers',
    component: () => import(/* webpackChunkName: "ForEmployers" */ '@/views/ForEmployers.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity for Employers — Discover Talent. Get Work Done.',
      description:
        'Source raw talent from local schools. Get real projects completed. Zero time managing. Build your talent pipeline while making community impact.',
      metaTags: [
        {
          name: 'description',
          content:
            'Source raw talent from local schools. Get real projects completed. Zero time managing. Build your talent pipeline while making community impact.'
        },
        {
          name: 'keywords',
          content:
            'talent discovery, internship programs, high school recruiting, work-based learning, scalable hiring, evergreen projects, talent pipeline, community impact, employer programs, intern management'
        },
        {
          property: 'og:title',
          content: 'PilotCity for Employers — Talent Discovery, Automated'
        },
        {
          property: 'og:description',
          content:
            'Create internship programs in minutes. Students complete real projects. You discover talent and get work delivered. Zero management overhead.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-employers.jpg'
        },
        {
          name: 'theme-color',
          content: '#10B981'
        }
      ]
    }
  },
  {
    path: '/for-teachers',
    name: 'for-teachers',
    component: () => import(/* webpackChunkName: "ForTeachers" */ '@/views/ForTeachers.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity for Teachers — Bring the Real World Into Your Classroom',
      description:
        'Deploy work-based learning in minutes. Meet CTE standards. Give students real internships and opportunities — with zero hassle.',
      metaTags: [
        {
          name: 'description',
          content:
            'Deploy work-based learning in minutes. Meet CTE standards. Give students real internships and opportunities — with zero hassle.'
        },
        {
          name: 'keywords',
          content:
            'CTE teachers, work-based learning, career technical education, classroom internships, employer partnerships, student opportunities, curriculum programs, CTE compliance, pathway programs, teacher resources'
        },
        {
          property: 'og:title',
          content: 'PilotCity for Teachers — Work-Based Learning, Simplified'
        },
        {
          property: 'og:description',
          content:
            'Connect with local employers instantly. Deploy WBL programs in one click. Track student progress in real-time. CTE-approved and grant-ready.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-teachers.jpg'
        },
        {
          name: 'theme-color',
          content: '#3B82F6'
        }
      ]
    }
  },
  {
    path: '/for-schools',
    name: 'for-schools',
    component: () => import(/* webpackChunkName: "ForSchools" */ '@/views/ForSchools.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity for Schools — Scale Work-Based Learning Across Your Entire School',
      description:
        'Turnkey CTE solution. Meet state standards. Win grants. Deploy WBL at scale with zero overhead. 50+ schools already transforming student outcomes.',
      metaTags: [
        {
          name: 'description',
          content:
            'Turnkey CTE solution. Meet state standards. Win grants. Deploy WBL at scale with zero overhead. 50+ schools already transforming student outcomes.'
        },
        {
          name: 'keywords',
          content:
            'CTE school administration, work-based learning programs, school CTE compliance, grant applications, state standards, scalable WBL, turnkey CTE solution, school administrator tools, career pathways, CTE outcomes'
        },
        {
          property: 'og:title',
          content: 'PilotCity for Schools — Turnkey Work-Based Learning at Scale'
        },
        {
          property: 'og:description',
          content:
            'Deploy WBL across all pathways. Meet state standards. Empower teachers. Track outcomes. Win grants. All in one platform.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-schools.jpg'
        },
        {
          name: 'theme-color',
          content: '#6366F1'
        }
      ]
    }
  },
  {
    path: '/for-parents',
    name: 'for-parents',
    component: () => import(/* webpackChunkName: "ForParents" */ '@/views/ForParents.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity for Parents — Give Your Child the Opportunities They Deserve',
      description:
        'Real internships. Portfolio building. College application boost. Safe, guided opportunities that prepare your child for success. 10,000+ students thriving.',
      metaTags: [
        {
          name: 'description',
          content:
            'Real internships. Portfolio building. College application boost. Safe, guided opportunities that prepare your child for success. 10,000+ students thriving.'
        },
        {
          name: 'keywords',
          content:
            'parent resources, student internships, college applications, career exploration, portfolio building, high school opportunities, safe internships, student success, college prep, career validation'
        },
        {
          property: 'og:title',
          content: "PilotCity for Parents — Real Opportunities for Your Child's Future"
        },
        {
          property: 'og:description',
          content:
            'Help your child build their resume, explore careers, and strengthen college applications. Safe, structured, and results-driven.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-parents.jpg'
        },
        {
          name: 'theme-color',
          content: '#F43F5E'
        }
      ]
    }
  },
  {
    path: '/for-college-prep',
    name: 'for-college-prep',
    component: () => import(/* webpackChunkName: "ForCollegePrep" */ '@/views/ForCollegePrep.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity for College Prep Programs — Guaranteed Internships for Your Families',
      description:
        'Partner with PilotCity to offer guaranteed internships, career validation, and college app boosters. Custom branding. Revenue sharing. Perfect for activity fairs.',
      metaTags: [
        {
          name: 'description',
          content:
            'Partner with PilotCity to offer guaranteed internships, career validation, and college app boosters. Custom branding. Revenue sharing. Perfect for activity fairs.'
        },
        {
          name: 'keywords',
          content:
            'college prep programs, college prep academies, guaranteed internships, college applications, career exploration, student portfolios, activity fairs, counselor resources, revenue sharing, partnership programs'
        },
        {
          property: 'og:title',
          content: 'PilotCity for College Prep — A Partnership That Delivers Value'
        },
        {
          property: 'og:description',
          content:
            'Offer families guaranteed internships and career validation. Custom branded landing pages. Revenue sharing opportunities. Measurable ROI.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-college-prep.jpg'
        },
        {
          name: 'theme-color',
          content: '#A855F7'
        }
      ]
    }
  },
  {
    path: '/for-chambers',
    name: 'for-chambers',
    component: () => import(/* webpackChunkName: "ForChambers" */ '@/views/ForChambers.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity for Chambers — Turn Your Network Into Revenue and Impact',
      description:
        'Generate non-dues revenue by creating internship programs for your members. Sell to schools and sponsors. 60% revenue share. Zero overhead. Become a civic engine.',
      metaTags: [
        {
          name: 'description',
          content:
            'Generate non-dues revenue by creating internship programs for your members. Sell to schools and sponsors. 60% revenue share. Zero overhead. Become a civic engine.'
        },
        {
          name: 'keywords',
          content:
            'chamber of commerce programs, non-dues revenue, workforce development, economic development, internship programs, member benefits, chamber partnerships, civic engagement, talent development, business community'
        },
        {
          property: 'og:title',
          content: 'PilotCity for Chambers — From Connector to Catalyst'
        },
        {
          property: 'og:description',
          content:
            'Create internship programs for members. Generate $6k-$30k+ in revenue. Bridge education and employment. Transform into a civic engine.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-chambers.jpg'
        },
        {
          name: 'theme-color',
          content: '#F59E0B'
        }
      ]
    }
  },
  {
    path: '/for-cities',
    name: 'for-cities',
    component: () => import(/* webpackChunkName: "ForCities" */ '@/views/ForCities.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity for Cities — The Operating System for Modern Cities',
      description:
        'Run programs. Engage citizens. Source talent. Data-driven, scalable, transparent. Digital transformation for city governments across all departments.',
      metaTags: [
        {
          name: 'description',
          content:
            'Run programs. Engage citizens. Source talent. Data-driven, scalable, transparent. Digital transformation for city governments across all departments.'
        },
        {
          name: 'keywords',
          content:
            'city government programs, digital transformation, civic engagement, public utility, workforce development, economic development, city operations, government technology, smart cities, public service, civic programs'
        },
        {
          property: 'og:title',
          content: 'PilotCity for Cities — Digital Transformation for Government'
        },
        {
          property: 'og:description',
          content:
            'The operating system for modern cities. Run programs across all departments. Track outcomes. Engage citizens. Source local talent. 50+ cities.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-cities.jpg'
        },
        {
          name: 'theme-color',
          content: '#8B5CF6'
        }
      ]
    }
  },
  {
    path: '/for-charter-schools',
    name: 'for-charter-schools',
    component: () =>
      import(/* webpackChunkName: "ForCharterSchools" */ '@/views/ForCharterSchools.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity for Charter Schools — Turnkey Out-of-Classroom Experiences',
      description:
        'Turnkey menu of out-of-classroom experiences. Real-world opportunities for expeditions, intersessions, and external learning. Minimal resources. Student choice.',
      metaTags: [
        {
          name: 'description',
          content:
            'Turnkey menu of out-of-classroom experiences. Real-world opportunities for expeditions, intersessions, and external learning. Minimal resources. Student choice.'
        },
        {
          name: 'keywords',
          content:
            'charter schools, out of classroom experiences, external learning, expeditions, intersessions, work-based learning, summit public schools, design tech, project-based learning, student choice, turnkey programs, independent study'
        },
        {
          property: 'og:title',
          content: 'PilotCity for Charter Schools — Out-of-Classroom Experiences Made Easy'
        },
        {
          property: 'og:description',
          content:
            'Complete menu of external experiences. Real company projects. Student choice. Minimal resources. Perfect for expeditions, intersessions, and more.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-charter-schools.jpg'
        },
        {
          name: 'theme-color',
          content: '#14B8A6'
        }
      ]
    }
  },
  {
    path: '/for-program-managers',
    name: 'for-program-managers',
    component: () =>
      import(/* webpackChunkName: "ForProgramManagers" */ '@/views/ForProgramManagers.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity for Program Managers — Stop Managing. Start Scaling.',
      description:
        'Design programs with AI. Automate delivery. Track outcomes. Scale infinitely. The platform that transforms program managers into program designers.',
      metaTags: [
        {
          name: 'description',
          content:
            'Design programs with AI. Automate delivery. Track outcomes. Scale infinitely. The platform that transforms program managers into program designers.'
        },
        {
          name: 'keywords',
          content:
            'program management, program design, autonomous programs, AI program builder, program automation, outcome tracking, scalable programs, program templates, cross-sector programs, nonprofit programs, education programs, government programs'
        },
        {
          property: 'og:title',
          content: 'PilotCity for Program Managers — From Managing to Designing'
        },
        {
          property: 'og:description',
          content:
            'Stop managing logistics. Start focusing on outcomes. Programs that run themselves with AI guidance, real-time tracking, and infinite scalability.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-program-managers.jpg'
        },
        {
          name: 'theme-color',
          content: '#8B5CF6'
        }
      ]
    }
  },
  {
    path: '/for-hospitals',
    name: 'for-hospitals',
    component: () => import(/* webpackChunkName: "ForHospitals" */ '@/views/ForHospitals.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity for Hospitals — Build the Future of Health Careers',
      description:
        'Connect with local youth. Source future healthcare talent. Create health career pathways and community wellness programs. Scalable, automated, impactful.',
      metaTags: [
        {
          name: 'description',
          content:
            'Connect with local youth. Source future healthcare talent. Create health career pathways and community wellness programs. Scalable, automated, impactful.'
        },
        {
          name: 'keywords',
          content:
            'hospital programs, health careers, healthcare talent pipeline, medical internships, community health, health education, nursing programs, youth healthcare, health career pathways, hospital community engagement'
        },
        {
          property: 'og:title',
          content: 'PilotCity for Hospitals — Health Career Programs That Scale'
        },
        {
          property: 'og:description',
          content:
            'Build health career pipelines with local youth. Create internships, education programs, and community wellness initiatives. Zero overhead.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-hospitals.jpg'
        },
        {
          name: 'theme-color',
          content: '#EF4444'
        }
      ]
    }
  },
  {
    path: '/for-young-entrepreneurs',
    name: 'for-young-entrepreneurs',
    component: () =>
      import(/* webpackChunkName: "ForYoungEntrepreneurs" */ '@/views/ForYoungEntrepreneurs.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title:
        "PilotCity for Young Entrepreneurs — Build Your AI Startup While You're Still in School",
      description:
        'Incubate your AI startup in high school or college. Find cofounders. Access investors. Join the network of youngest AI founders building the future.',
      metaTags: [
        {
          name: 'description',
          content:
            'Incubate your AI startup in high school or college. Find cofounders. Access investors. Join the network of youngest AI founders building the future.'
        },
        {
          name: 'keywords',
          content:
            'young entrepreneurs, AI startups, high school entrepreneurs, college startups, AI boom, cofounder matching, startup incubation, venture capital, angel investors, young founders, student entrepreneurs, AI companies, teen startups'
        },
        {
          property: 'og:title',
          content: 'PilotCity for Young Entrepreneurs — The Youngest AI Founders'
        },
        {
          property: 'og:description',
          content:
            "Build your AI startup while you're still in school. Access the youngest talent. Find cofounders. Get funded. The AI boom is now."
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-young-entrepreneurs.jpg'
        },
        {
          name: 'theme-color',
          content: '#A855F7'
        }
      ]
    }
  },
  {
    path: '/for-investors',
    name: 'for-investors',
    component: () => import(/* webpackChunkName: "ForInvestors" */ '@/views/ForInvestors.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity for Investors — Source the Youngest Entrepreneurial Talent',
      description:
        'Institutional access to high school and middle school founders. Raw talent from public school classrooms. The only network with this unique access.',
      metaTags: [
        {
          name: 'description',
          content:
            'Institutional access to high school and middle school founders. Raw talent from public school classrooms. The only network with this unique access.'
        },
        {
          name: 'keywords',
          content:
            'venture capital, angel investors, early stage investing, young entrepreneurs, high school founders, student startups, talent discovery, public school talent, raw talent sourcing, seed investments, investor network, young founders, entrepreneurship programs'
        },
        {
          property: 'og:title',
          content: 'PilotCity for Investors — Access the Youngest Talent Network'
        },
        {
          property: 'og:description',
          content:
            'Find future founders before anyone else. Institutional access to public school entrepreneurship programs. High school and middle school talent.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-investors.jpg'
        },
        {
          name: 'theme-color',
          content: '#F59E0B'
        }
      ]
    }
  },
  {
    path: '/how-it-works',
    name: 'how-it-works',
    component: () => import(/* webpackChunkName: "HowItWorks" */ '@/views/HowItWorks.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'How PilotCity Works — Network of People, Places & Programs',
      description:
        'Understand the PilotCity network. Three ways to participate: Join programs, Create opportunities, Sponsor outcomes. One platform. Infinite possibilities.',
      metaTags: [
        {
          name: 'description',
          content:
            'Understand the PilotCity network. Three ways to participate: Join programs, Create opportunities, Sponsor outcomes. One platform. Infinite possibilities.'
        },
        {
          name: 'keywords',
          content:
            'how pilotcity works, program network, participants creators sponsors, AI programs, program creation, community platform, opportunity network, digital transformation, autonomous programs, program lifecycle'
        },
        {
          property: 'og:title',
          content: 'How PilotCity Works — A Network of People, Places & Programs'
        },
        {
          property: 'og:description',
          content:
            'Join programs as a participant. Create with AI as a creator. Fund outcomes as a sponsor. Three ways to transform your community.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-how-it-works.jpg'
        },
        {
          name: 'theme-color',
          content: '#22D3EE'
        }
      ]
    }
  },
  {
    path: '/cities',
    name: 'cities',
    component: () => import(/* webpackChunkName: "Cities" */ '@/views/Cities.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Cities — Build Your Hometown Into a City of Innovation',
      description:
        'Explore the PilotCity network of 50+ cities building innovation engines. From San Leandro to Sacramento, discover how cities transform through programs, people, and places.',
      metaTags: [
        {
          name: 'description',
          content:
            'Explore the PilotCity network of 50+ cities building innovation engines. From San Leandro to Sacramento, discover how cities transform through programs, people, and places.'
        },
        {
          name: 'keywords',
          content:
            'pilotcity cities, innovation cities, digital twin, city network, economic development, workforce development, civic engagement, city programs, hometown innovation, local opportunities'
        },
        {
          property: 'og:title',
          content: 'PilotCity Cities — Network of Innovation Hubs'
        },
        {
          property: 'og:description',
          content:
            '50+ cities building innovation engines. Discover how your city can join the network and transform through programs, people, and places.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-cities.jpg'
        },
        {
          name: 'theme-color',
          content: '#A855F7'
        }
      ]
    }
  },
  {
    path: '/stories-landing',
    name: 'stories-landing',
    component: () => import(/* webpackChunkName: "StoriesLanding" */ '@/views/StoriesLanding.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Stories — Real Transformations from the PilotCity Network',
      description:
        'Read transformation stories from 10,000+ participants, creators, and sponsors. From invisible to visible. From student to professional. Real people. Real programs. Real impact.',
      metaTags: [
        {
          name: 'description',
          content:
            'Read transformation stories from 10,000+ participants, creators, and sponsors. From invisible to visible. From student to professional. Real people. Real programs. Real impact.'
        },
        {
          name: 'keywords',
          content:
            'pilotcity stories, transformation stories, student success, internship stories, career transformation, personal growth, professional development, testimonials, success stories, opportunity impact'
        },
        {
          property: 'og:title',
          content: 'PilotCity Stories — Transformations That Inspire'
        },
        {
          property: 'og:description',
          content:
            'From invisible to visible. From unsure to confident. Real transformation stories from the PilotCity network.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-stories.jpg'
        },
        {
          name: 'theme-color',
          content: '#F59E0B'
        }
      ]
    }
  },
  {
    path: '/personas',
    name: 'personas',
    component: () => import(/* webpackChunkName: "Personas" */ '@/views/Personas.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Personas — PilotCity is for Everyone',
      description:
        'Discover how PilotCity serves students, teachers, employers, schools, parents, cities, chambers, hospitals, and everyone in your hometown ecosystem. Find your role in the network.',
      metaTags: [
        {
          name: 'description',
          content:
            'Discover how PilotCity serves students, teachers, employers, schools, parents, cities, chambers, hospitals, and everyone in your hometown ecosystem. Find your role in the network.'
        },
        {
          name: 'keywords',
          content:
            'pilotcity personas, user types, students, teachers, employers, schools, parents, cities, chambers, hospitals, program managers, everyone, community network, education industry government community'
        },
        {
          property: 'og:title',
          content: 'PilotCity Personas — For Everyone in Your City'
        },
        {
          property: 'og:description',
          content:
            'From students to CEOs, teachers to city staff — PilotCity serves everyone in your hometown ecosystem. Find your role.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-personas.jpg'
        },
        {
          name: 'theme-color',
          content: '#06B6D4'
        }
      ]
    }
  },
  {
    path: '/programs',
    name: 'programs',
    component: () =>
      import(/* webpackChunkName: "ExplorePrograms" */ '@/views/ExplorePrograms.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Explore Programs — Your Hometown Marketplace for Opportunity',
      description:
        '500+ programs created by your community. Internships, projects, courses, and experiences. Join programs, build your portfolio, earn credentials, and transform your future.',
      metaTags: [
        {
          name: 'description',
          content:
            '500+ programs created by your community. Internships, projects, courses, and experiences. Join programs, build your portfolio, earn credentials, and transform your future.'
        },
        {
          name: 'keywords',
          content:
            'explore programs, internships, projects, courses, opportunities, program marketplace, build projects, earn internships, portfolio building, career programs, work-based learning'
        },
        {
          property: 'og:title',
          content: 'Explore Programs — 500+ Opportunities from Your Community'
        },
        {
          property: 'og:description',
          content:
            'Browse internships, projects, and experiences created by your community. Programs = opportunity with instructions included.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-programs.jpg'
        },
        {
          name: 'theme-color',
          content: '#10B981'
        }
      ]
    }
  },
  {
    path: '/login-landing',
    name: 'login-landing',
    component: () => import(/* webpackChunkName: "LoginLanding" */ '@/views/LoginLanding.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Log In — Welcome Back to PilotCity',
      description:
        'Log in to your PilotCity account. Continue building your future through programs, people, and places in your hometown network.',
      metaTags: [
        {
          name: 'description',
          content:
            'Log in to your PilotCity account. Continue building your future through programs, people, and places in your hometown network.'
        },
        {
          name: 'keywords',
          content: 'pilotcity login, sign in, account access, user login, member login'
        },
        {
          property: 'og:title',
          content: 'Log In to PilotCity'
        },
        {
          property: 'og:description',
          content: 'Welcome back. Continue your transformation journey.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-login.jpg'
        },
        {
          name: 'theme-color',
          content: '#22D3EE'
        }
      ]
    }
  },
  {
    path: '/get-started',
    name: 'get-started',
    component: () => import(/* webpackChunkName: "GetStarted" */ '@/views/GetStarted.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Get Started — Join the PilotCity Network',
      description:
        'Create your PilotCity account. Access 500+ programs, build your portfolio, earn credentials, and join 10,000+ people transforming their futures.',
      metaTags: [
        {
          name: 'description',
          content:
            'Create your PilotCity account. Access 500+ programs, build your portfolio, earn credentials, and join 10,000+ people transforming their futures.'
        },
        {
          name: 'keywords',
          content:
            'pilotcity signup, create account, join pilotcity, get started, register, new account, join network'
        },
        {
          property: 'og:title',
          content: 'Get Started with PilotCity — Join the Network'
        },
        {
          property: 'og:description',
          content:
            'Join 10,000+ participants, creators, and sponsors. Access 500+ programs. Start your transformation today.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-signup.jpg'
        },
        {
          name: 'theme-color',
          content: '#10B981'
        }
      ]
    }
  },
  {
    path: '/downtown-analytics',
    name: 'downtown-analytics',
    component: () =>
      import(/* webpackChunkName: "DowntownAnalytics" */ '@/views/DowntownAnalytics.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Downtown Analytics — Track Every Pedestrian, Vehicle & Transaction in Real-Time',
      description:
        'AI-powered sensors and computer vision technology track economic activity in your downtown, building, or campus. Make data-driven decisions for economic development with real-time analytics.',
      metaTags: [
        {
          name: 'description',
          content:
            'AI-powered sensors and computer vision technology track economic activity in your downtown, building, or campus. Make data-driven decisions for economic development with real-time analytics.'
        },
        {
          name: 'keywords',
          content:
            'downtown analytics, smart city, IoT sensors, computer vision, economic development, foot traffic data, pedestrian counting, city planning, property analytics, real estate data, traffic analysis, downtown data, sensor technology, urban analytics, business intelligence, economic activity tracking'
        },
        {
          property: 'og:title',
          content: 'Downtown Analytics — Smart City Infrastructure for Economic Development'
        },
        {
          property: 'og:description',
          content:
            'Track every pedestrian, vehicle, and transaction with AI-powered sensors. Real-time data for cities, property owners, and businesses.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-downtown-analytics.jpg'
        },
        {
          name: 'theme-color',
          content: '#3B82F6'
        }
      ]
    }
  },
  {
    path: '/ai-success-manager',
    name: 'ai-success-manager',
    component: () =>
      import(/* webpackChunkName: "AISuccessManager" */ '@/views/AISuccessManager.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'AI Success Manager — Every Participant Gets a Personal AI Coach',
      description:
        'Multi-channel AI assistant that guides participants through programs via SMS, email, and phone. Context-aware, 24/7 support that knows their progress and helps them succeed.',
      metaTags: [
        {
          name: 'description',
          content:
            'Multi-channel AI assistant that guides participants through programs via SMS, email, and phone. Context-aware, 24/7 support that knows their progress and helps them succeed.'
        },
        {
          name: 'keywords',
          content:
            'AI program manager, AI success manager, participant assistant, program completion, multi-channel communication, SMS coaching, AI mentor, program guidance, participant support, automated coaching, Twilio integration, program automation, student success, completion rates'
        },
        {
          property: 'og:title',
          content: 'AI Success Manager — 24/7 Participant Coaching at Scale'
        },
        {
          property: 'og:description',
          content:
            'Give every participant a personal AI coach. Multi-channel support via SMS, email, and phone. 80% completion rates.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-ai-success-manager.jpg'
        },
        {
          name: 'theme-color',
          content: '#A855F7'
        }
      ]
    }
  },
  {
    path: '/build-projects-earn-internships',
    name: 'build-projects-earn-internships',
    component: () =>
      import(
        /* webpackChunkName: "BuildProjectsEarnInternships" */ '@/views/BuildProjectsEarnInternships.vue'
      ),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Build Projects to Earn Internships - Earn the Title by Delivering the Work',
      description:
        'Complete real-world projects for employers and earn guaranteed internship credentials. Self-paced, AI-coached, and run with 8,598 participants to date. Perfect for middle school through post-secondary.',
      metaTags: [
        {
          name: 'description',
          content:
            'Complete real-world projects for employers and earn guaranteed internship credentials. Self-paced, AI-coached, and run with 8,598 participants to date. Perfect for middle school through post-secondary.'
        },
        {
          name: 'keywords',
          content:
            'guaranteed internships, high school internships, student projects, work-based learning, CTE programs, internship credentials, project-based internships, earn internships, portfolio building, college applications, youth employment, self-paced internships, AI coaching, program model, program template'
        },
        {
          property: 'og:title',
          content: 'Build Projects to Earn Internships - Self-Paced. Reviewed. Earned.'
        },
        {
          property: 'og:description',
          content:
            'Stop applying to 100+ positions. Build one real project. 8,598 participants to date.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-build-projects-internships.jpg'
        },
        {
          name: 'theme-color',
          content: '#06B6D4'
        }
      ]
    }
  },
  {
    path: '/advise-to-earn-civic-seal',
    name: 'advise-to-earn-civic-seal',
    component: () =>
      import(/* webpackChunkName: "AdviseToEarnCivicSeal" */ '@/views/AdviseToEarnCivicSeal.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Advise to Earn a Civic Seal — CTE Student Advisory Program Model for K-12 Districts',
      description:
        'Structured program where CTE students provide input to shape district programs and earn State of California Civic Seals. Flexible delivery: self-guided online + monthly in-person experiences.',
      metaTags: [
        {
          name: 'description',
          content:
            'Structured program where CTE students provide input to shape district programs and earn State of California Civic Seals. Flexible delivery: self-guided online + monthly in-person experiences.'
        },
        {
          name: 'keywords',
          content:
            'civic seal, CTE student advisory, work-based learning, student voice, district programs, Sacramento City Unified, CTE programs, student advisor, career technical education, student feedback, program model, K-12 programs, civic engagement, student leadership, district improvement'
        },
        {
          property: 'og:title',
          content: 'Advise to Earn a Civic Seal — Student Voice Meets Recognition'
        },
        {
          property: 'og:description',
          content:
            'CTE students provide input. Districts improve programs. Students earn State Civic Seals. Win-win-win.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-civic-seal.jpg'
        },
        {
          name: 'theme-color',
          content: '#A855F7'
        }
      ]
    }
  },
  {
    path: '/scusd-cte-advisory-platform',
    name: 'scusd-cte-advisory-platform',
    component: () =>
      import(/* webpackChunkName: "AdviseToEarnCivicSeal2" */ '@/views/AdviseToEarnCivicSeal2.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'CTE Student Voice Platform — Scalable District Feedback & Analytics',
      description:
        'PilotCity helps K-12 districts digitize CTE Student Advisory programs. Automate student input collection, issue Civic Seals, scale across all schools with data-driven insights.',
      metaTags: [
        {
          name: 'description',
          content:
            "Turn Sacramento City Unified's CTE Student Advisory from manual one-off to scalable district asset. Digitize student input, automate Civic Seals, replicate across all schools."
        },
        {
          name: 'keywords',
          content:
            'Sacramento City Unified, SCUSD, CTE platform, civic seal automation, student advisory software, district program management, CTE data analytics, student voice platform, program scalability, K-12 district technology, work-based learning platform'
        },
        {
          property: 'og:title',
          content: 'CTE Student Voice Platform — Powered by PilotCity'
        },
        {
          property: 'og:description',
          content:
            'How PilotCity helps SCUSD scale CTE Student Advisory across all high schools. Automated, data-driven, repeatable.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-scusd-platform.jpg'
        },
        {
          name: 'theme-color',
          content: '#7C3AED'
        }
      ]
    }
  },
  {
    path: '/cte-student-voice',
    name: 'cte-student-voice',
    component: () =>
      import(/* webpackChunkName: "AdviseToEarnCivicSeal3" */ '@/views/AdviseToEarnCivicSeal3.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'CTE Student Voice — Give Every Student a Say in Their Future',
      description:
        'Help Sacramento City Unified scale CTE Student Advisory with PilotCity. Students shape programs, earn Civic Seals, and districts get data-driven insights. From pilot to district-wide.',
      metaTags: [
        {
          name: 'description',
          content:
            'Help Sacramento City Unified scale CTE Student Advisory with PilotCity. Students shape programs, earn Civic Seals, and districts get data-driven insights. From pilot to district-wide.'
        },
        {
          name: 'keywords',
          content:
            'CTE student voice, student-centered CTE, Sacramento City Unified, civic seal program, student advisory platform, CTE feedback, district analytics, student leadership, career technical education, K-12 program platform, student engagement'
        },
        {
          property: 'og:title',
          content: 'CTE Student Voice — Students Shape Programs. Districts Get Data.'
        },
        {
          property: 'og:description',
          content:
            'Turn student voice into district insights. Automated, scalable, and meaningful. Built for SCUSD, ready for any district.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-cte-student-voice.jpg'
        },
        {
          name: 'theme-color',
          content: '#8B5CF6'
        }
      ]
    }
  },
  {
    path: '/cte-local-employer-projects',
    name: 'cte-local-employer-projects',
    component: () =>
      import(
        /* webpackChunkName: "CTELocalEmployerProjects" */ '@/views/CTELocalEmployerProjects.vue'
      ),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Local Employer Projects for CTE — Instant Menu for Your Classroom',
      description:
        'CTE teachers: build a real-world project for your pathway in minutes. Deploy real-world PBL without recruiting employers. Traction-driven model builds your WBL network automatically.',
      metaTags: [
        {
          name: 'description',
          content:
            'CTE teachers: build a real-world project for your pathway in minutes. Deploy real-world PBL without recruiting employers. Traction-driven model builds your WBL network automatically.'
        },
        {
          name: 'keywords',
          content:
            'CTE teacher tools, local employer projects, work-based learning, project-based learning, CTE curriculum, employer recruitment, traction-driven recruitment, WBL flywheel, classroom internships, CTE pathway projects, real-world learning, K-12 employer partnerships'
        },
        {
          property: 'og:title',
          content: 'Local Employer Projects — Instant WBL for CTE Classrooms'
        },
        {
          property: 'og:description',
          content:
            '50+ local projects. 2-minute setup. Zero employer calls. Deploy real-world PBL today.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-cte-local-projects.jpg'
        },
        {
          name: 'theme-color',
          content: '#10B981'
        }
      ]
    }
  },
  {
    path: '/advise-to-earn-civic-seal-v4',
    name: 'advise-to-earn-civic-seal-v4',
    component: () =>
      import(/* webpackChunkName: "AdviseToEarnCivicSeal4" */ '@/views/AdviseToEarnCivicSeal4.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title:
        'CTE Student Advisory Platform — From Pilot to District-Wide | Sacramento City Unified',
      description:
        'Sacramento City Unified: Scale your CTE Student Advisory program with PilotCity. Students provide input, earn Civic Seals. Structured, repeatable, district-wide infrastructure.',
      metaTags: [
        {
          name: 'description',
          content:
            'Sacramento City Unified: Scale your CTE Student Advisory program with PilotCity. Students provide input, earn Civic Seals. Structured, repeatable, district-wide infrastructure.'
        },
        {
          name: 'keywords',
          content:
            'Sacramento City Unified, SCUSD, CTE Student Advisory, Civic Seal, student voice platform, CTE program feedback, district program management, CTE analytics, student input platform, work-based learning feedback, K-12 CTE programs, student advisory infrastructure, Burbank High School, Hiram Johnson High School'
        },
        {
          property: 'og:title',
          content: 'CTE Student Advisory — From One-Off Pilot to District-Wide Program'
        },
        {
          property: 'og:description',
          content:
            'Students shape CTE programs and earn State Civic Seals. PilotCity makes it structured, repeatable, and scalable across all SCUSD high schools.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-scusd-cte-advisory.jpg'
        },
        {
          name: 'theme-color',
          content: '#9333EA'
        }
      ]
    }
  },
  {
    path: '/citywide-wbl-master-plan',
    name: 'citywide-wbl-master-plan',
    component: () =>
      import(/* webpackChunkName: "CitywideWBLMasterPlan" */ '@/views/CitywideWBLMasterPlan.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Citywide Work-Based Learning Master Plan — Infrastructure That Lasts Decades',
      description:
        'K-12 districts: Get consultation + blueprint + platform for sustainable WBL infrastructure. Connects schools, employers, chambers, and city government. Immediate opportunities + long-term strategy.',
      metaTags: [
        {
          name: 'description',
          content:
            'K-12 districts: Get consultation + blueprint + platform for sustainable WBL infrastructure. Connects schools, employers, chambers, and city government. Immediate opportunities + long-term strategy.'
        },
        {
          name: 'keywords',
          content:
            'work-based learning infrastructure, WBL master plan, K-12 district planning, CTE architecture, sustainable WBL, employer partnerships, citywide WBL, district WBL strategy, workforce development, early workforce system, WBL ecosystem, K-12 consultation, WBL blueprint, district services'
        },
        {
          property: 'og:title',
          content: 'Citywide WBL Master Plan — Stop Recreating the Wheel Every Year'
        },
        {
          property: 'og:description',
          content:
            'Sustainable WBL infrastructure for K-12 districts. Immediate opportunities + long-term strategy. Built to last decades.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-citywide-wbl.jpg'
        },
        {
          name: 'theme-color',
          content: '#10B981'
        }
      ]
    }
  },
  {
    path: '/ai-digital-transformation',
    name: 'ai-digital-transformation',
    component: () =>
      import(
        /* webpackChunkName: "AIDigitalTransformation" */ '@/views/AIDigitalTransformation.vue'
      ),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'AI Digital Transformation Projects for Students — Build Skills, Earn Internships',
      description:
        'Students: Use AI to help local employers transform digitally. Build real solutions for manufacturing, healthcare, retail, city government. Earn internships. Position yourself as AI talent.',
      metaTags: [
        {
          name: 'description',
          content:
            'Students: Use AI to help local employers transform digitally. Build real solutions for manufacturing, healthcare, retail, city government. Earn internships. Position yourself as AI talent.'
        },
        {
          name: 'keywords',
          content:
            'AI student projects, AI internships, digital transformation, student AI talent, AI portfolio projects, high school AI, AI for business, ChatGPT projects, AI automation, student opportunities, AI boom, local employer AI, AI skills development, tech internships, AI transformation'
        },
        {
          property: 'og:title',
          content: 'AI Digital Transformation — Students Helping Employers Adopt AI'
        },
        {
          property: 'og:description',
          content:
            'You understand AI. Local employers need it. Build solutions. Earn internships. The AI boom is your moment.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-ai-transformation.jpg'
        },
        {
          name: 'theme-color',
          content: '#A855F7'
        }
      ]
    }
  },
  {
    path: '/hackathon-internship-track',
    name: 'hackathon-internship-track',
    component: () =>
      import(
        /* webpackChunkName: "HackathonInternshipTrack" */ '@/views/HackathonInternshipTrack.vue'
      ),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Hackathon Internship Track — Guaranteed Internships for Your Event',
      description:
        'Hackathon organizers: Embed guaranteed internship track into your event. Participants earn real internships. You earn 25% revenue share. Turnkey solution. Unique differentiator.',
      metaTags: [
        {
          name: 'description',
          content:
            'Hackathon organizers: Embed guaranteed internship track into your event. Participants earn real internships. You earn 25% revenue share. Turnkey solution. Unique differentiator.'
        },
        {
          name: 'keywords',
          content:
            'hackathon internships, hackathon tracks, guaranteed internships, hackathon revenue, student hackathons, internship track, hackathon prizes, college hackathons, high school hackathons, hackathon organizers, event partnerships, hackathon differentiation, résumé builder, hackathon platform'
        },
        {
          property: 'og:title',
          content: 'Hackathon Internship Track — The Prize Everyone Actually Wants'
        },
        {
          property: 'og:description',
          content:
            'Embed guaranteed internships into your hackathon. Participants earn credentials. You earn revenue. Stand out.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-hackathon.jpg'
        },
        {
          name: 'theme-color',
          content: '#F97316'
        }
      ]
    }
  },
  // OAuth consent for hosted MCP connectors (claude.ai, ChatGPT): Nexus /oauth/authorize sends a
  // signed-out browser here with the OAuth query. The page stashes the query, opens the auth
  // dialog itself (useAuthOrchestrator().openAuth) and restores the query afterwards
  // (requiresAuth's redirect query would corrupt `state`, and requiresUser dead-ends an
  // incomplete profile). App.vue's onboarding auto-open exempts this path. See views/OAuthConsent.vue.
  {
    path: '/oauth/consent',
    name: 'oauthConsent',
    component: () => import(/* webpackChunkName: "OAuthConsent" */ '@/views/OAuthConsent.vue'),
    meta: { layout: 'no-nav', requiresAuth: false, requiresUser: false, title: 'Allow access | PilotCity' }
  },
  {
    path: '/cofoundership',
    name: 'cofoundership',
    component: () => import(/* webpackChunkName: "Cofoundership" */ '@/views/Cofoundership.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Cofoundership | PilotCity',
      description:
        'PilotCity co-founds companies with its alumni. You lead and build; we bring first users, first customers and investor introductions.',
      metaTags: [
        {
          name: 'description',
          content:
            'PilotCity co-founds companies with its alumni. You lead and build; we bring first users, first customers and investor introductions.'
        },
        { property: 'og:title', content: 'Cofoundership' },
        { property: 'og:image', content: 'https://widgets.pilotcity.com/og/cofoundership-v1.png' }
      ]
    }
  },
  {
    path: '/ai-program-manager',
    name: 'ai-program-manager',
    component: () =>
      import(/* webpackChunkName: "AIProgramManager" */ '@/views/AIProgramManager.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'AI Program Manager | PilotCity',
      description:
        'Text it what you want to run. It builds the plan, adds your people, and follows up with every one of them, right in your texts.',
      metaTags: [
        { name: 'robots', content: 'noindex' },
        {
          name: 'description',
          content:
            'Text it what you want to run. It builds the plan, adds your people, and follows up with every one of them, right in your texts.'
        },
        { property: 'og:title', content: 'Meet your AI program manager' },
        {
          property: 'og:image',
          content: 'https://widgets.pilotcity.com/og/ai-program-manager-v1.png'
        }
      ]
    }
  },
  {
    path: '/gold',
    name: 'gold-map',
    component: () => import(/* webpackChunkName: "GoldMap" */ '@/views/GoldMap.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: "The gold mine nobody's looking in. | PilotCity",
      description:
        "Undiscovered talent in Bay Area classrooms. Describe who you're looking for and the map shows you who's already here, live from 7,000+ profiles.",
      metaTags: [
        { name: 'robots', content: 'noindex' },
        {
          name: 'description',
          content:
            "Undiscovered talent in Bay Area classrooms. Describe who you're looking for and the map shows you who's already here, live from 7,000+ profiles."
        },
        { property: 'og:title', content: "The gold mine nobody's looking in." },
        {
          property: 'og:description',
          content:
            "Undiscovered talent in Bay Area classrooms. Describe who you're looking for and the map shows you who's already here, live from 7,000+ profiles."
        },
        { property: 'og:image', content: 'https://widgets.pilotcity.com/og/gold-v3.png' }
      ]
    }
  },
  {
    path: '/postings',
    name: 'postings-inverted',
    component: () =>
      import(/* webpackChunkName: "PostingsInverted" */ '@/views/PostingsInverted.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Instead of reading their resumes, read what they built. | PilotCity',
      description:
        'A job board where the postings are people and the proof of work they made for real companies. Search it like a recruiter.',
      metaTags: [
        { name: 'robots', content: 'noindex' },
        {
          name: 'description',
          content:
            'A job board where the postings are people and the proof of work they made for real companies. Search it like a recruiter.'
        },
        {
          property: 'og:title',
          content: 'Instead of reading their resumes, read what they built.'
        },
        {
          property: 'og:description',
          content:
            'A job board where the postings are people and the proof of work they made for real companies. Search it like a recruiter.'
        },
        { property: 'og:image', content: 'https://widgets.pilotcity.com/og/postings-v1.png' }
      ]
    }
  },
  {
    path: '/letter',
    name: 'gold-letter',
    component: () => import(/* webpackChunkName: "GoldLetter" */ '@/views/GoldLetter.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'A letter to whoever is hiring. | PilotCity',
      description:
        "One page from Derick: who we have met since 2016, and a P.S. that answers who you're looking for.",
      metaTags: [
        { name: 'robots', content: 'noindex' },
        {
          name: 'description',
          content:
            "One page from Derick: who we have met since 2016, and a P.S. that answers who you're looking for."
        },
        { property: 'og:title', content: 'A letter to whoever is hiring.' },
        {
          property: 'og:description',
          content:
            "One page from Derick: who we have met since 2016, and a P.S. that answers who you're looking for."
        },
        { property: 'og:image', content: 'https://widgets.pilotcity.com/og/letter-v1.png' }
      ]
    }
  },
  {
    path: '/assay',
    name: 'undiscovered-talent',
    component: () =>
      import(/* webpackChunkName: "UndiscoveredTalent" */ '@/views/UndiscoveredTalent.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Your job description is a syllabus. | PilotCity',
      description:
        'Every posting is a list of things you wish someone could do. We turn it into a program, run it in Bay Area classrooms, and introduce you to the people who did the things.',
      metaTags: [
        { name: 'robots', content: 'noindex' },
        {
          name: 'description',
          content:
            'Every posting is a list of things you wish someone could do. We turn it into a program, run it in Bay Area classrooms, and introduce you to the people who did the things.'
        },
        { property: 'og:title', content: 'Your job description is a syllabus.' },
        {
          property: 'og:description',
          content:
            'Every posting is a list of things you wish someone could do. We turn it into a program, run it in Bay Area classrooms, and introduce you to the people who did the things.'
        },
        { property: 'og:image', content: 'https://widgets.pilotcity.com/og/assay-v1.png' }
      ]
    }
  },
  {
    path: '/seeking',
    name: 'seeking-offering',
    component: () =>
      import(/* webpackChunkName: "SeekingOffering" */ '@/views/SeekingOffering.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Meet the agent that connects people. | PilotCity',
      description:
        "Tell it what you're seeking and what you can offer. It finds who you should meet, which opportunities fit, and the ecosystems you already belong to.",
      metaTags: [
        { name: 'robots', content: 'noindex' },
        {
          name: 'description',
          content:
            "Tell it what you're seeking and what you can offer. It finds who you should meet, which opportunities fit, and the ecosystems you already belong to."
        },
        { property: 'og:title', content: 'Meet the agent that connects people.' },
        {
          property: 'og:description',
          content:
            "Tell it what you're seeking and what you can offer. It finds who you should meet, which opportunities fit, and the ecosystems you already belong to."
        },
        { property: 'og:image', content: 'https://widgets.pilotcity.com/og/seeking-v2.png' }
      ]
    }
  },
  {
    path: '/classroom',
    name: 'classroom-free',
    component: () => import(/* webpackChunkName: "ClassroomFree" */ '@/views/ClassroomFree.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity, free for your classroom | PilotCity',
      description:
        'Real projects from real companies, run in your room, on your schedule. Your students build; the companies watch.',
      metaTags: [
        { name: 'robots', content: 'noindex' },
        {
          name: 'description',
          content:
            'Real projects from real companies, run in your room, on your schedule. Your students build; the companies watch.'
        },
        { property: 'og:title', content: 'PilotCity, free for your classroom' },
        {
          property: 'og:description',
          content:
            'Real projects from real companies, run in your room, on your schedule. Your students build; the companies watch.'
        },
        { property: 'og:image', content: 'https://widgets.pilotcity.com/og/classroom-v1.png' }
      ]
    }
  },
  {
    // The Master Narrative (2026-10-04) as a page. DRAFT: keep it unlinked until
    // the founder approves the narrative and its public-page fixes 1-3 ship.
    path: '/what-we-do',
    name: 'what-we-do',
    component: () => import(/* webpackChunkName: "WhatWeDo" */ '@/views/WhatWeDo.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Real projects for students. Real introductions for alumni. | PilotCity',
      description:
        'PilotCity turns real job descriptions into class projects, then introduces our alumni to people who can open doors.',
      metaTags: [
        { name: 'robots', content: 'noindex' },
        {
          property: 'og:title',
          content: 'PilotCity: real projects for students, real introductions for alumni'
        },
        {
          property: 'og:description',
          content:
            'PilotCity turns real job descriptions into class projects, then introduces our alumni to people who can open doors.'
        },
        { property: 'og:image', content: 'https://widgets.pilotcity.com/og/what-we-do-v1.png' }
      ]
    }
  },
  {
    path: '/cognition',
    name: 'cognition-associates',
    component: () =>
      import(/* webpackChunkName: "CognitionAssociates" */ '@/views/CognitionAssociates.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Cognition Applied AI associates - Oct 28, San Francisco | PilotCity',
      description:
        'A private invitation for PilotCity alumni. Two years or more of real engineering and a teaching signal. Say so and we put you in the room.',
      metaTags: [
        { name: 'robots', content: 'noindex' },
        {
          name: 'description',
          content:
            'A private invitation for PilotCity alumni. Two years or more of real engineering and a teaching signal. Say so and we put you in the room.'
        },
        {
          property: 'og:title',
          content: 'Cognition Applied AI associates - Oct 28, San Francisco'
        },
        {
          property: 'og:description',
          content:
            'A private invitation for PilotCity alumni. Two years or more of real engineering and a teaching signal. Say so and we put you in the room.'
        },
        { property: 'og:image', content: 'https://widgets.pilotcity.com/og/cognition-v1.png' }
      ]
    }
  },
  {
    path: '/qualified',
    name: 'qualifications',
    component: () => import(/* webpackChunkName: "Qualifications" */ '@/views/Qualifications.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Know who qualifies before they apply | PilotCity',
      description:
        'Write your eligibility in plain words. We match it against what we already know about every person on PilotCity, field by field, with the proof behind it.',
      metaTags: [
        { name: 'robots', content: 'noindex' },
        {
          name: 'description',
          content:
            'Write your eligibility in plain words. We match it against what we already know about every person on PilotCity, field by field, with the proof behind it.'
        },
        { property: 'og:title', content: 'Know who qualifies before they apply' },
        {
          property: 'og:description',
          content:
            'Write your eligibility in plain words. We match it against what we already know about every person on PilotCity, field by field, with the proof behind it.'
        },
        { property: 'og:image', content: 'https://widgets.pilotcity.com/og/qualified-v1.png' }
      ]
    }
  },
  {
    path: '/for-agents',
    name: 'ai-program-manager-agents',
    component: () =>
      import(/* webpackChunkName: "AIProgramManagerAgents" */ '@/views/AIProgramManagerAgents.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Run people from your AI agent | PilotCity',
      description:
        'A program manager your AI agent can call over MCP or an API. It enrolls people, texts them, follows up, and waits until the step is done. Consent first.',
      metaTags: [
        { name: 'robots', content: 'noindex' },
        {
          name: 'description',
          content:
            'A program manager your AI agent can call over MCP or an API. It enrolls people, texts them, follows up, and waits until the step is done. Consent first.'
        },
        { property: 'og:title', content: 'Your AI can do the work. Now it can run the people.' },
        {
          property: 'og:image',
          content: 'https://widgets.pilotcity.com/og/for-agents-v1.png'
        }
      ]
    }
  },
  {
    path: '/ai-program-manager-chat',
    name: 'ai-program-manager-chat',
    component: () =>
      import(/* webpackChunkName: "AIProgramManagerChat" */ '@/views/AIProgramManagerChat.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Meet your program manager | PilotCity',
      description:
        'Meet your program manager. It lives in your texts: plans the program, adds your people, and does all the following up.',
      metaTags: [
        { name: 'robots', content: 'noindex' },
        {
          name: 'description',
          content:
            'Meet your program manager. It lives in your texts: plans the program, adds your people, and does all the following up.'
        },
        { property: 'og:title', content: 'Meet your program manager. It lives in your texts.' },
        {
          property: 'og:image',
          content: 'https://widgets.pilotcity.com/og/ai-program-manager-v1.png'
        }
      ]
    }
  },
  {
    path: '/ai-program-manager-walk',
    name: 'ai-program-manager-walk',
    component: () =>
      import(/* webpackChunkName: "AIProgramManagerWalk" */ '@/views/AIProgramManagerWalk.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Programs, made effortless | PilotCity',
      description:
        'An AI program manager for schools, cities and employers. It plans the program, texts every participant, and tells you only what needs you.',
      metaTags: [
        { name: 'robots', content: 'noindex' },
        {
          name: 'description',
          content:
            'An AI program manager for schools, cities and employers. It plans the program, texts every participant, and tells you only what needs you.'
        },
        { property: 'og:title', content: 'Programs, made effortless' },
        {
          property: 'og:image',
          content: 'https://widgets.pilotcity.com/og/ai-program-manager-walk-v1.png'
        }
      ]
    }
  },
  {
    path: '/overnight-ai-takeover',
    name: 'overnight-ai-takeover',
    component: () =>
      import(/* webpackChunkName: "OvernightAITakeover" */ '@/views/OvernightAITakeover.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Overnight AI Campus Takeover | PilotCity',
      description:
        'Take over a campus for a weekend. Build AI for real buildings, spaces and sponsor challenges, and stay overnight on site. 18+.',
      metaTags: [
        { name: 'robots', content: 'noindex' },
        {
          name: 'description',
          content:
            'Take over a campus for a weekend. Build AI for real buildings, spaces and sponsor challenges, and stay overnight on site. 18+.'
        },
        { property: 'og:title', content: 'Overnight AI Campus Takeover' },
        {
          property: 'og:image',
          content: 'https://widgets.pilotcity.com/og/overnight-ai-takeover-v2.png'
        }
      ]
    }
  },
  {
    path: '/physical-ai-lab',
    name: 'physical-ai-lab',
    component: () => import(/* webpackChunkName: "PhysicalAILab" */ '@/views/PhysicalAILab.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Physical AI Lab | PilotCity',
      description:
        'Build the robots, cameras and sensors that make places smarter. Four-week build sprints on Seeed Studio hardware, run by PilotCity.',
      metaTags: [
        { name: 'robots', content: 'noindex' },
        {
          name: 'description',
          content:
            'Build the robots, cameras and sensors that make places smarter. Four-week build sprints on Seeed Studio hardware, run by PilotCity.'
        },
        { property: 'og:title', content: 'Physical AI Lab' },
        { property: 'og:image', content: 'https://widgets.pilotcity.com/og/physical-ai-lab-v2.png' }
      ]
    }
  },
  {
    path: '/city-hall-ai-hackathon',
    name: 'city-hall-ai-hackathon',
    component: () =>
      import(/* webpackChunkName: "CityHallAIHackathon" */ '@/views/CityHallAIHackathon.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'City Hall AI Hackathon | PilotCity',
      description:
        'Local engineers and CS students spend a day at City Hall building AI prototypes for real city problems, starting from tools the city already uses.',
      metaTags: [
        {
          name: 'description',
          content:
            'Local engineers and CS students spend a day at City Hall building AI prototypes for real city problems, starting from tools the city already uses.'
        },
        { property: 'og:title', content: 'City Hall AI Hackathon' },
        {
          property: 'og:image',
          content: 'https://widgets.pilotcity.com/og/city-hall-ai-hackathon-v2.png'
        }
      ]
    }
  },
  {
    path: '/schoolwide-wbl-assembly',
    name: 'schoolwide-wbl-assembly',
    component: () =>
      import(/* webpackChunkName: "SchoolwideWBLAssembly" */ '@/views/SchoolwideWBLAssembly.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Schoolwide WBL Assembly — Work-Based Learning for Every Student in One Day',
      description:
        'K-12 schools: Onboard your entire student population with a 1-hour assembly. Students brainstorm solutions for real employers, build portfolios, and kickstart your employer partnership engine.',
      metaTags: [
        {
          name: 'description',
          content:
            'K-12 schools: Onboard your entire student population with a 1-hour assembly. Students brainstorm solutions for real employers, build portfolios, and kickstart your employer partnership engine.'
        },
        {
          name: 'keywords',
          content:
            'schoolwide assembly, work-based learning, K-12 WBL, career day, student onboarding, employer partnerships, project-based learning, school assembly, CTE assembly, career exploration, student portfolio, traction-driven recruitment, school-to-career, middle school WBL, high school WBL'
        },
        {
          property: 'og:title',
          content: 'Schoolwide WBL Assembly — 100% Participation in 1 Hour'
        },
        {
          property: 'og:description',
          content:
            'Give every student work-based learning in one assembly. Portfolio projects. Employer data. Platform onboarding. Risk-free pilot.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-schoolwide-assembly.jpg'
        },
        {
          name: 'theme-color',
          content: '#06B6D4'
        }
      ]
    }
  },
  {
    path: '/field-trip-maximizer',
    name: 'field-trip-maximizer',
    component: () =>
      import(/* webpackChunkName: "FieldTripMaximizer" */ '@/views/FieldTripMaximizer.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Field Trip Maximizer — Turn Field Trips Into Employer Partnerships',
      description:
        'CTE teachers: Students brainstorm solutions before field trips, pitch on-site, get employer mentorship. 4x WBL outcomes from one field trip. Maximal Value Delivery (MVD) model.',
      metaTags: [
        {
          name: 'description',
          content:
            'CTE teachers: Students brainstorm solutions before field trips, pitch on-site, get employer mentorship. 4x WBL outcomes from one field trip. Maximal Value Delivery (MVD) model.'
        },
        {
          name: 'keywords',
          content:
            'field trips, CTE field trips, work-based learning, employer partnerships, student pitching, project-based learning, employer mentorship, traction-driven recruitment, TDER, MVD, maximal value delivery, K-12 field trips, career exposure, CTE teacher tools, partnership building'
        },
        {
          property: 'og:title',
          content: 'Field Trip Maximizer — 4x Outcomes from One Field Trip'
        },
        {
          property: 'og:description',
          content:
            'Students brainstorm solutions, pitch to employers, get mentorship, receive tour. Turn field trips into partnerships.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-field-trip-maximizer.jpg'
        },
        {
          name: 'theme-color',
          content: '#9333EA'
        }
      ]
    }
  },
  {
    path: '/ceo-guest-speaking',
    name: 'ceo-guest-speaking',
    component: () =>
      import(/* webpackChunkName: "CEOGuestSpeaking" */ '@/views/CEOGuestSpeaking.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'CEO Guest Speaking — Spark Student Ambition & Build Partnership Momentum',
      description:
        'Schools & districts: Book PilotCity CEO Derick Lee for a high-impact inspirational talk. Build student buy-in, spark ambition, kickstart partnerships. 60-70% student motivation. High-touch onboarding.',
      metaTags: [
        {
          name: 'description',
          content:
            'Schools & districts: Book PilotCity CEO Derick Lee for a high-impact inspirational talk. Build student buy-in, spark ambition, kickstart partnerships. 60-70% student motivation. High-touch onboarding.'
        },
        {
          name: 'keywords',
          content:
            'guest speaker, CEO speaking, student motivation, career inspiration, school assembly, partnership onboarding, work-based learning, student buy-in, CTE guest speaker, career day, founder keynote, high school speaker, district speaker, career exploration, purpose discovery'
        },
        {
          property: 'og:title',
          content: 'CEO Guest Speaking — Ignite Student Ambition in One Session'
        },
        {
          property: 'og:description',
          content:
            'PilotCity CEO comes to your school. Delivers unforgettable talk. Builds student buy-in. Kickstarts WBL momentum.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-ceo-speaking.jpg'
        },
        {
          name: 'theme-color',
          content: '#F59E0B'
        }
      ]
    }
  },
  {
    path: '/employer-recruitment-services',
    name: 'employer-recruitment-services',
    component: () =>
      import(
        /* webpackChunkName: "EmployerRecruitmentServices" */ '@/views/EmployerRecruitmentServices.vue'
      ),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Employer Recruitment Services — We Build Partnerships for K-12 Schools',
      description:
        'K-12 schools & districts: Outsource employer recruitment to PilotCity. We use TDER model—students build projects, we deliver to employers, partnerships form. Managed service. Proven strategy.',
      metaTags: [
        {
          name: 'description',
          content:
            'K-12 schools & districts: Outsource employer recruitment to PilotCity. We use TDER model—students build projects, we deliver to employers, partnerships form. Managed service. Proven strategy.'
        },
        {
          name: 'keywords',
          content:
            'employer recruitment, K-12 employer partnerships, WBL partnerships, CTE employer recruitment, work-based learning partnerships, managed WBL services, employer outreach, partnership building, TDER model, traction-driven recruitment, employer engagement, K-12 services, WBL coordinator, CTE services'
        },
        {
          property: 'og:title',
          content: 'Employer Recruitment Services — We Do It For You'
        },
        {
          property: 'og:description',
          content:
            'Outsource employer recruitment. Students build projects. We deliver to employers. Partnerships built. Zero cold calls.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-employer-recruitment.jpg'
        },
        {
          name: 'theme-color',
          content: '#A855F7'
        }
      ]
    }
  },
  {
    path: '/public-school-classrooms',
    name: 'public-school-classrooms',
    component: () =>
      import(/* webpackChunkName: "PublicSchoolClassrooms" */ '@/views/PublicSchoolClassrooms.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Public School Classrooms — The Engine for Every Community | PilotCity Vision',
      description:
        'The public school classroom is the fundamental building block of every city. Activate classrooms to solve real problems for local employers. Build an early workforce engine. Transform your community.',
      metaTags: [
        {
          name: 'description',
          content:
            'The public school classroom is the fundamental building block of every city. Activate classrooms to solve real problems for local employers. Build an early workforce engine. Transform your community.'
        },
        {
          name: 'keywords',
          content:
            'public school classrooms, K-12 innovation, workforce engine, early workforce development, classroom activation, CTE classrooms, real-world PBL, project-based learning, local employer projects, economic development, talent development, community building, civic infrastructure, PilotCity vision, silo busting, K-12 city government chambers'
        },
        {
          property: 'og:title',
          content: "Public School Classrooms — Your City's Greatest Asset"
        },
        {
          property: 'og:description',
          content:
            'Every city has classrooms. Mobilize them to solve real problems. Build workforce engines. Transform communities. The PilotCity vision.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-classrooms.jpg'
        },
        {
          name: 'theme-color',
          content: '#10B981'
        }
      ]
    }
  },
  {
    path: '/employer-opportunity-hub',
    name: 'employer-opportunity-hub',
    component: () =>
      import(/* webpackChunkName: "EmployerOpportunityHub" */ '@/views/EmployerOpportunityHub.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Employer Opportunity Hub — Stop Saying No to Schools | Set Up Once, Scale Forever',
      description:
        'Employers: Create your menu of internships, field trips, guest speaking, career programs in <1 hour. Share one link. Students self-enroll. Programs run autonomously. Zero ongoing time. Infinite scaling.',
      metaTags: [
        {
          name: 'description',
          content:
            'Employers: Create your menu of internships, field trips, guest speaking, career programs in <1 hour. Share one link. Students self-enroll. Programs run autonomously. Zero ongoing time. Infinite scaling.'
        },
        {
          name: 'keywords',
          content:
            'employer WBL programs, scalable internships, employer opportunity menu, automated field trips, virtual guest speaking, career fair platform, employer talent pipeline, K-12 employer partnerships, autonomous WBL delivery, employer CSR programs, talent sourcing automation, work-based learning for employers, employer opportunity hub, scale WBL programs'
        },
        {
          property: 'og:title',
          content: 'Employer Opportunity Hub — Say Yes Without the Time Commitment'
        },
        {
          property: 'og:description',
          content:
            'Set up once. Scale forever. Create your WBL opportunity menu in <1 hour. Zero ongoing management.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-employer-hub.jpg'
        },
        {
          name: 'theme-color',
          content: '#A855F7'
        }
      ]
    }
  },
  {
    path: '/employer-prioritization',
    name: 'employer-prioritization',
    component: () =>
      import(/* webpackChunkName: "EmployerPrioritization" */ '@/views/EmployerPrioritization.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title:
        'Employer Prioritization Framework — Strategic Roadmap for K-12 WBL Recruitment | PilotCity',
      description:
        'K-12 institutions: Stop cold calling random employers. Use the strategic prioritization framework. Start with contacts you have. Target infrastructure partners. TDER model. Proven across 1,200+ schools.',
      metaTags: [
        {
          name: 'description',
          content:
            'K-12 institutions: Stop cold calling random employers. Use the strategic prioritization framework. Start with contacts you have. Target infrastructure partners. TDER model. Proven across 1,200+ schools.'
        },
        {
          name: 'keywords',
          content:
            'employer prioritization, WBL recruitment strategy, K-12 employer partnerships, strategic employer targeting, infrastructure partners, city government partnerships, chamber partnerships, hospital partnerships, TDER framework, employer recruitment roadmap, WBL strategic planning, CTE employer recruitment, systematic employer engagement, workforce development partnerships'
        },
        {
          property: 'og:title',
          content: 'Employer Prioritization — Stop Cold Calling. Start Strategic.'
        },
        {
          property: 'og:description',
          content:
            "The framework for K-12 WBL recruitment. Start where the yes is easiest. Scale where it's most valuable."
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-employer-prioritization.jpg'
        },
        {
          name: 'theme-color',
          content: '#06B6D4'
        }
      ]
    }
  },
  {
    path: '/activity-type-matrix',
    name: 'activity-type-matrix',
    component: () =>
      import(/* webpackChunkName: "ActivityTypeMatrix" */ '@/views/ActivityTypeMatrix.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Activity Type Matrix — How PilotCity Delivers All Types of Programs | Framework',
      description:
        'The 2×2 framework for program delivery. Online/in-person × Async/sync. PilotCity supports all activity types — from infinite-scale async/online to high-touch sync/in-person. Flexibility. Scalability. Maximum impact.',
      metaTags: [
        {
          name: 'description',
          content:
            'The 2×2 framework for program delivery. Online/in-person × Async/sync. PilotCity supports all activity types — from infinite-scale async/online to high-touch sync/in-person. Flexibility. Scalability. Maximum impact.'
        },
        {
          name: 'keywords',
          content:
            'activity type matrix, program delivery framework, asynchronous learning, synchronous learning, online programs, in-person programs, scalable WBL, flexible program delivery, program design matrix, PilotCity framework, async online activities, sync online activities, async in-person, sync in-person, program scalability, TDER framework, WBL delivery modes'
        },
        {
          property: 'og:title',
          content: 'Activity Type Matrix — All Activities. All Programs.'
        },
        {
          property: 'og:description',
          content:
            'The 2×2 framework explaining how PilotCity delivers programs across all modalities and interaction types. Scalability + flexibility + impact.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-activity-matrix.jpg'
        },
        {
          name: 'theme-color',
          content: '#6366F1'
        }
      ]
    }
  },
  {
    path: '/flexible-program-durations',
    name: 'flexible-program-durations',
    component: () =>
      import(/* webpackChunkName: "FlexibleDurations" */ '@/views/FlexibleDurations.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Flexible Program Durations — Programs for Every Schedule | PilotCity',
      description:
        'K-12 institutions: Deploy programs of any duration. 1-minute games to 1-year transformations. Practice Zone to Engage Zone. Build momentum. Meet participants where they are.',
      metaTags: [
        {
          name: 'description',
          content:
            'K-12 institutions: Deploy programs of any duration. 1-minute games to 1-year transformations. Practice Zone to Engage Zone. Build momentum. Meet participants where they are.'
        },
        {
          name: 'keywords',
          content:
            'flexible program durations, program durations, flexible programs, 1-hour programs, 1-week programs, 1-month programs, practice zone, engage zone, progression pathway, completion rates, program on-ramps, curriculum flexibility, buy-in building, momentum building, scalable WBL, K-12 program delivery, CTE programs, work-based learning roadmap, program sequences, WBL continuum'
        },
        {
          property: 'og:title',
          content: 'Flexible Program Durations — All Schedules. All Success Rates.'
        },
        {
          property: 'og:description',
          content:
            'From 1-minute games to 1-year transformations. Meet participants where they are. Build momentum. Scale outcomes.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-flexible-durations.jpg'
        },
        {
          name: 'theme-color',
          content: '#6366F1'
        }
      ]
    }
  },
  {
    path: '/maximal-value-delivery',
    name: 'maximal-value-delivery',
    component: () =>
      import(/* webpackChunkName: "MaximalValueDelivery" */ '@/views/MaximalValueDelivery.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Maximal Value Delivery (MVD) — Strategic Program Design Framework | PilotCity',
      description:
        'The methodology for designing initiatives that maximize value for all stakeholders. Strategic sequencing + resource leverage + multi-stakeholder wins. Turn 1 outcome into 4+. TDER amplifier.',
      metaTags: [
        {
          name: 'description',
          content:
            'The methodology for designing initiatives that maximize value for all stakeholders. Strategic sequencing + resource leverage + multi-stakeholder wins. Turn 1 outcome into 4+. TDER amplifier.'
        },
        {
          name: 'keywords',
          content:
            'maximal value delivery, MVD framework, program design, initiative design, strategic sequencing, resource leverage, multi-stakeholder value, win-win scenarios, program sequences, TDER amplifier, field trip maximizer, guest speaker maximizer, WBL design, program strategy, initiative strategy, K-12 program design, employer engagement strategy, value multiplication'
        },
        {
          property: 'og:title',
          content: 'Maximal Value Delivery — Turn 1 Outcome Into 4+'
        },
        {
          property: 'og:description',
          content:
            'The framework for designing initiatives that maximize value through strategic sequencing. MVD + TDER = unstoppable.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-mvd.jpg'
        },
        {
          name: 'theme-color',
          content: '#A855F7'
        }
      ]
    }
  },
  {
    path: '/warm-introductions-network',
    name: 'warm-introductions-network',
    component: () =>
      import(
        /* webpackChunkName: "WarmIntroductionsNetwork" */ '@/views/WarmIntroductionsNetwork.vue'
      ),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Warm Introductions Network — Automatic Connections for Your City | PilotCity',
      description:
        "Stop cold networking. Get warm introductions to anyone in your city through your network's network. SMS-first. Portfolio-powered. AI discovers connection paths automatically.",
      metaTags: [
        {
          name: 'description',
          content:
            "Stop cold networking. Get warm introductions to anyone in your city through your network's network. SMS-first. Portfolio-powered. AI discovers connection paths automatically."
        },
        {
          name: 'keywords',
          content:
            'warm introductions, social networking, SMS introductions, text networking, connection engine, network navigator, social capital, portfolio connections, automated introductions, city networking, professional connections, mentor connections, investor connections, talent connections, warm intro app, connection algorithm'
        },
        {
          property: 'og:title',
          content: 'Warm Introductions Network — Your Network × Their Network'
        },
        {
          property: 'og:description',
          content:
            'Connect with anyone in your city through automatic warm introductions. SMS-first. Portfolio-powered. The original PilotCity vision.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-warm-intros.jpg'
        },
        {
          name: 'theme-color',
          content: '#3B82F6'
        }
      ]
    }
  },
  {
    path: '/guest-speaker-maximizer',
    name: 'guest-speaker-maximizer',
    component: () =>
      import(/* webpackChunkName: "GuestSpeakerMaximizer" */ '@/views/GuestSpeakerMaximizer.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Guest Speaker Maximizer — Turn 1 Hour Into Unlimited Programs | PilotCity',
      description:
        'K-12 solution: Record guest speakers as interviews. Extract authentic problems. Create scalable programs. Students build solutions. MVD + TDER = employer partnerships.',
      metaTags: [
        {
          name: 'description',
          content:
            'K-12 solution: Record guest speakers as interviews. Extract authentic problems. Create scalable programs. Students build solutions. MVD + TDER = employer partnerships.'
        },
        {
          name: 'keywords',
          content:
            'guest speaker maximizer, maximize guest speakers, K-12 guest speakers, employer interviews, MVD guest speaking, TDER guest speakers, scalable WBL content, reusable guest speaker content, initiative design, program sequences, employer partnership building, CTE guest speakers, work-based learning scalability, guest speaker ROI, interview-style guest speaking'
        },
        {
          property: 'og:title',
          content: 'Guest Speaker Maximizer — 1 Hour → Infinite Value'
        },
        {
          property: 'og:description',
          content:
            'Stop wasting guest speakers. Record as interviews. Extract problems. Students build solutions. Turn finite resources into unlimited programs.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-guest-speaker-maximizer.jpg'
        },
        {
          name: 'theme-color',
          content: '#A855F7'
        }
      ]
    }
  },
  {
    path: '/autonomous-programs',
    name: 'autonomous-programs',
    component: () =>
      import(/* webpackChunkName: "AutonomousPrograms" */ '@/views/AutonomousPrograms.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Autonomous Programs — Stop Managing. Start Scaling. | PilotCity',
      description:
        'The platform for creating programs that run themselves. AI-guided participants. Auto-credentialing. Zero ongoing management. For program managers across every sector.',
      metaTags: [
        {
          name: 'description',
          content:
            'The platform for creating programs that run themselves. AI-guided participants. Auto-credentialing. Zero ongoing management. For program managers across every sector.'
        },
        {
          name: 'keywords',
          content:
            'autonomous programs, program management, program automation, AI program delivery, self-running programs, program logic model, program templates, program managers, scalable programs, managerless programs, program creation platform, program design, initiative design, organizational management, K-12 programs, business programs, government programs, nonprofit programs, education programs, workforce programs, autonomous delivery'
        },
        {
          property: 'og:title',
          content: 'Autonomous Programs — Programs That Run Themselves'
        },
        {
          property: 'og:description',
          content:
            'Create once. Deploy unlimited times. Zero management. The platform for program managers who want to scale impact, not logistics.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-autonomous-programs.jpg'
        },
        {
          name: 'theme-color',
          content: '#06B6D4'
        }
      ]
    }
  },
  {
    path: '/talent-pool-builder',
    name: 'talent-pool-builder',
    component: () =>
      import(/* webpackChunkName: "TalentPoolBuilder" */ '@/views/TalentPoolBuilder.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Talent Pool Builder — Every Program Builds a Community | PilotCity',
      description:
        'The hidden benefit of autonomous programs. Run 1 program, build a talent pool automatically. Pre-filtered, pre-trained, ready-to-hire talent. Zero recruiting effort.',
      metaTags: [
        {
          name: 'description',
          content:
            'The hidden benefit of autonomous programs. Run 1 program, build a talent pool automatically. Pre-filtered, pre-trained, ready-to-hire talent. Zero recruiting effort.'
        },
        {
          name: 'keywords',
          content:
            'talent pool, talent acquisition, community building, employer talent pipeline, intern community, talent pool builder, automatic recruiting, talent pipeline automation, program communities, employer hiring, pre-filtered talent, talent network, alumni network, hiring pipeline, workforce pipeline, talent discovery, automated talent acquisition, community of interns, program-based recruiting'
        },
        {
          property: 'og:title',
          content: 'Talent Pool Builder — Stop Recruiting. Start Building Communities.'
        },
        {
          property: 'og:description',
          content:
            '1 autonomous program + 50 participants = 50-person talent pool. Pre-trained. Pre-filtered. Ready to hire. Zero effort.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-talent-pool.jpg'
        },
        {
          name: 'theme-color',
          content: '#A855F7'
        }
      ]
    }
  },
  {
    path: '/vocabulary',
    name: 'vocabulary',
    component: () =>
      import(/* webpackChunkName: "PilotCityVocabulary" */ '@/views/PilotCityVocabulary.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title:
        'The Language of PilotCity — Understanding Programs, People, Places | Vocabulary Guide',
      description:
        'Learn the vocabulary of PilotCity. Components → Activities → Programs → Initiatives. Participants, Creators, Sponsors. The words that power your new hometown.',
      metaTags: [
        {
          name: 'description',
          content:
            'Learn the vocabulary of PilotCity. Components → Activities → Programs → Initiatives. Participants, Creators, Sponsors. The words that power your new hometown.'
        },
        {
          name: 'keywords',
          content:
            'PilotCity vocabulary, program terminology, platform glossary, components activities programs initiatives, participants creators sponsors, program logic model, program architecture, platform language, understanding PilotCity, program structure, organizational management, program hierarchy, network terminology, digital twin vocabulary'
        },
        {
          property: 'og:title',
          content: 'The Language of PilotCity — Vocabulary Guide'
        },
        {
          property: 'og:description',
          content:
            'Understand the words that power PilotCity. Components. Activities. Programs. Initiatives. People. Places. Outcomes.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-vocabulary.jpg'
        },
        {
          name: 'theme-color',
          content: '#06B6D4'
        }
      ]
    }
  },
  {
    path: '/solution-architecture',
    name: 'solution-architecture',
    component: () =>
      import(/* webpackChunkName: "SolutionArchitecture" */ '@/views/SolutionArchitecture.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Solution Architecture & Roadmap — 23 Solutions, 40 Features, $10M Vision | PilotCity',
      description:
        'The complete strategic blueprint for PilotCity. User journeys, feature requirements, persona mapping, and monetization strategy. From landing pages to $10M revenue.',
      metaTags: [
        {
          name: 'description',
          content:
            'The complete strategic blueprint for PilotCity. User journeys, feature requirements, persona mapping, and monetization strategy. From landing pages to $10M revenue.'
        },
        {
          name: 'keywords',
          content:
            'PilotCity roadmap, solution architecture, product strategy, feature requirements, user journeys, monetization strategy, revenue model, platform blueprint, product roadmap, strategic planning, feature matrix, persona mapping, go-to-market strategy'
        },
        {
          property: 'og:title',
          content: 'Solution Architecture — The Complete PilotCity Blueprint'
        },
        {
          property: 'og:description',
          content:
            '23 solutions. 40 features. $10M vision. The strategic roadmap for building, monetizing, and scaling PilotCity.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-solution-architecture.jpg'
        },
        {
          name: 'theme-color',
          content: '#06B6D4'
        }
      ]
    }
  },
  {
    path: '/tder-methodology',
    name: 'tder-methodology',
    component: () =>
      import(/* webpackChunkName: "TDERMethodology" */ '@/views/TDERMethodology.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'TDER — Traction-Driven Employer Recruitment for Work-Based Learning',
      description:
        'The methodology that solves the "No" problem in WBL. Students build value first. Employers engage after. Partnerships form naturally. Invented by PilotCity. Tested across 1,200+ schools.',
      metaTags: [
        {
          name: 'description',
          content:
            'The methodology that solves the "No" problem in WBL. Students build value first. Employers engage after. Partnerships form naturally. Invented by PilotCity. Tested across 1,200+ schools.'
        },
        {
          name: 'keywords',
          content:
            'TDER, traction-driven recruitment, employer recruitment, work-based learning, WBL methodology, employer engagement, K-12 WBL, chicken and egg problem, WBL flywheel, employer partnerships, CTE methodology, workforce development, talent pipeline, PilotCity methodology'
        },
        {
          property: 'og:title',
          content: 'TDER — Solve the "No" Problem in Work-Based Learning'
        },
        {
          property: 'og:description',
          content:
            'Students build first. Employers engage after. The flywheel that scales WBL partnerships.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-tder.jpg'
        },
        {
          name: 'theme-color',
          content: '#06B6D4'
        }
      ]
    }
  },
  {
    path: '/',
    name: 'landing',
    // Host-split landing: pilotcity.ai serves Home16; pilotcity.com (and any
    // other host, including localhost) serves Home30 — the cream/ink Create
    // landing. The Story landing (TheRoom) lives at /room. Home18 is parked at
    // /home18; Landing.vue remains at /explore (legacy).
    component: () =>
      window.location.hostname === 'pilotcity.ai'
        ? import(/* webpackChunkName: "Home16" */ '@/views/Home16.vue')
        : import(/* webpackChunkName: "Home30" */ '@/views/Home30.vue'),
    props: route => ({
      preSelectedTemplate: route.query.template
    }),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/room',
    name: 'the-room',
    component: () => import(/* webpackChunkName: "TheRoom" */ '@/views/TheRoom.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'There is undiscovered talent in every classroom. | PilotCity',
      description:
        'A network for talent, a platform for programs. Young people build real work for real companies in their classrooms; hirers describe who they are looking for and the network answers.',
      metaTags: [
        { name: 'robots', content: 'noindex' },
        {
          property: 'og:title',
          content: 'There is undiscovered talent in every classroom.'
        },
        {
          property: 'og:description',
          content:
            'A network for talent, a platform for programs. Young people build real work for real companies in their classrooms; hirers describe who they are looking for and the network answers.'
        },
        { property: 'og:image', content: 'https://widgets.pilotcity.com/og/room-v1.png' }
      ]
    }
  },
  {
    path: '/ecc2022',
    name: 'Ecc2022',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/Ecc2022.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/stemday',
    name: 'StemDay',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/StemDay.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/employer',
    name: 'EmployerLanding',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/EmployerLanding.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/help/employer',
    name: 'EmployerHelpLanding',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/EmployerHelpLanding.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/teacher',
    name: 'TeacherLanding',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/TeacherLanding.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/help/teacher',
    name: 'TeacherHelpLanding',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/TeacherHelpLanding.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/student',
    name: 'StudentLanding',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/StudentLanding.vue'),
    meta: {
      layout: 'no-nav'
    }
  },

  {
    path: '/stories',
    name: 'Stories',
    component: () => import(/* webpackChunkName: "Landing" */ '../views/Stories.vue'),
    meta: {
      layout: 'no-nav'
    }
  },

  {
    path: '/story',
    name: 'Story',
    component: () => import(/* webpackChunkName: "Landing" */ '../views/Story.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/show',
    name: 'Show',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/Show.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/help/student',
    name: 'StudentHelpLanding',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/StudentHelpLanding.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/parents',
    name: 'ParentLanding',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/ParentLanding.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/school',
    name: 'SchoolLanding',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/SchoolLanding.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/partner',
    name: 'PartnerLanding',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/PartnerLanding.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  // {
  //   path: '/sponsor',
  //   name: 'SponsorLanding',
  //   component: () => import(/* webpackChunkName: "Landing" */ '@/views/SponsorLanding.vue'),
  //   meta: {
  //     layout: 'no-nav'
  //   }
  // },
  {
    path: '/onboard2023',
    name: 'Onboard2023',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/Onboard2023.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/quote',
    name: 'SponsorQuote',
    component: () => import('@/views/Sponsor.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    },
    props: route => ({
      email: route.query.email
      // example: http://localhost:8080/sponsor?email=dericklee@wehope.org
    })
  },
  {
    path: '/alumni',
    name: 'alumniJoin',
    component: () => import('@/views/AlumniForm.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/team',
    name: 'Alumni',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/Alumni.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/youtube',
    name: 'youtubeRedirect',
    beforeEnter() {
      window.location.href = 'https://youtube.com/pilotcity/shorts';
    }
  },
  // TEAM SAFETY, RULES, ETC
  {
    path: '/gate510safetytraining',
    name: 'Gate510safetytraining',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/Gate510safetytraining.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  // CONTRIBUTOR / ALUMNI TEAM ORIENTATION
  {
    path: '/orientation',
    name: 'AlumniTeamOrientation',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/AlumniTeamOrientation.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/contributor',
    name: 'contributor',
    component: () =>
      import(/* webpackChunkName: "Portfolio" */ '@/views/Portfolio/Contributor2.vue'),
    meta: {
      // requiresAuth: true,
      layout: 'no-nav'
      // requiresUser: true
    }
  },
  // EVENTS 2022
  {
    path: '/ebia2022',
    name: 'Ebia2022',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/Ebia2022.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/airtime2022recap',
    name: 'Airtime2022Recap',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/Airtime2022.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/airtime2022',
    name: 'Airtime2022',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/Airtime2022.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/sponsorpage',
    name: 'SponsorPage',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/SponsorPage.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/successteam',
    name: 'SuccessTeam',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/SuccessTeam.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/getsponsored',
    name: 'ApproveSponsor',
    component: () => import('@/views/ApproveSponsor.vue'),
    meta: {
      requiresAuth: true,
      layout: 'no-nav'
    }
  },
  {
    path: '/mobile',
    name: 'Mobile',
    component: () => import('@/views/Mobile.vue'),
    meta: {
      requiresAuth: true,
      layout: 'no-nav'
    }
  },
  {
    path: '/monitor-internship',
    name: 'MobileEmployer',
    component: () => import('@/views/MobileEmployer.vue'),
    meta: {
      requiresAuth: true,
      layout: 'no-nav'
    }
  },
  {
    path: '/sponsor/:shareCode',
    name: 'Sponsor',
    // component: () => import(/* webpackChunkName: "Guide" */ '@/views/Explore/Guide.vue'), // ! change this explore programs!!
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/Landing.vue'), // ! change this explore programs!!
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    },
    // beforeEnter(to, from, next) {
    //   const { getUser } = useUserGetters(['getUser']);
    //   if (getUser.value) {
    //     next();
    //   } else {
    //     localStorage.setItem('sponsorShipLinkPath', to.path);
    //     next({ name: 'login' });
    //   }
    // },
    props: route => ({
      shareCode: route.params.shareCode,
      // Deep-link support: allow URL to specify which tab to show and a pre-saved roadmap to load
      initialTab: (route.query.tab as string) || null,
      preloadRoadmapId: (route.query.roadmapId as string) || null
    })
  },
  {
    path: '/invite/:schoolName',
    name: 'invite',
    component: () => import(/* webpackChunkName: "Invite" */ '@/views/Invite.vue'),
    props: true,
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/invites/:partnerName',
    name: 'partnerInvite',
    component: () => import(/* webpackChunkName: "Invite" */ '@/views/PartnerInvite.vue'),
    props: true,
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/emailconfirmation',
    name: 'confirmEmail',
    component: () => import(/* webpackChunkName: "ConfirmEmail" */ '@/views/ConfirmEmail.vue'),
    props: route => ({
      token: route.query.token,
      tokenId: route.query.tokenId,
      username: route.query.username
    })
  },
  {
    path: '/changepassword',
    name: 'passwordChange',
    component: () => import(/* webpackChunkName: "ChangePassword" */ '@/views/ChangePassword.vue')
  },
  {
    path: '/discordconfirmation',
    name: 'confirmDiscord',
    component: () => import(/* webpackChunkName: "ConfirmDiscord" */ '@/views/ConfirmDiscord.vue'),
    props: route => ({
      code: route.query.code
    }),
    meta: {
      layout: 'no-nav',
      requiresAuth: false
    }
  },
  {
    path: '/password-reset',
    name: 'resetPassword',
    component: () => import(/* webpackChunkName: "ResetPassword" */ '@/views/ResetPassword.vue'),
    props: route => ({
      token: route.query.token,
      tokenId: route.query.tokenId
    })
  },
  {
    path: '/meetings',
    name: 'meetings',
    component: () => import(/* webpackChunkName: "Portfolio" */ '@/views/MeetingEvents.vue'),
    meta: {
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/privacy-policy',
    name: 'privacy-policy',
    component: () => import(/* webpackChunkName: "Portfolio" */ '@/views/PrivacyPolicy.vue')
  },
  {
    path: '/terms-and-conditions',
    name: 'terms-and-conditions',
    component: () => import(/* webpackChunkName: "Portfolio" */ '@/views/TermsAndConditions.vue')
  },
  {
    path: '/tour',
    name: 'tour',
    component: () => import(/* webpackChunkName: "Tour" */ '@/views/Tour.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/meet',
    name: 'meet',
    component: () => import(/* webpackChunkName: "Tour" */ '@/views/Tour.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/summer2025',
    name: 'summer2025Form',
    component: () => import('@/views/Summer2025Form.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/upi',
    name: 'upiForm',
    component: () => import('@/views/Summer2025Form.vue'),
    alias: ['/UPI', '/Upi', '/uPi', '/upI', '/UPi', '/UpI'],
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/insight',
    name: 'insightForm',
    component: () => import('@/views/Summer2025Form.vue'),
    alias: ['/INSIGHT', '/Insight'],
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/kaiser2025',
    name: 'kaiser2025Form',
    component: () => import('@/views/Kaiser2025Form.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/summeronboard',
    name: 'summerOnboardForm',
    component: () => import('@/views/SummerOnboardForm.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/why-join',
    name: 'whyJoin',
    component: () => import(/* webpackChunkName: "WhyJoin" */ '@/views/WhyJoin.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  // ============================================================
  // PUBLIC PROFILES DIRECTORY
  // ============================================================
  {
    path: '/directory',
    name: 'directory',
    /* Story 9.5: merged into /people, which carries the meta. Kept as a redirect
       rather than deleted - it is a public URL that has been shared, and a 404
       is a worse outcome than a hop. */
    redirect: { name: 'people' }
  },
  // ============================================================
  // PLACE PROFILE — Scope B+ /home18 Places lens detail page.
  // URL: /place/san-leandro-ca (slug = "city-state" lowercased).
  // Public. Backed by `placeProfile(slug)` GraphQL query.
  // ============================================================
  {
    path: '/place/:slug',
    name: 'place-profile',
    component: () =>
      import(/* webpackChunkName: "PlaceProfile" */ '@/views/Place/PlaceProfile.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'Place — PilotCity'
    }
  },
  {
    path: '/authRequired',
    name: 'authError',
    component: () => import(/* webpackChunkName: "ErrorLogin" */ '@/views/ErrorLogin.vue')
  },
  {
    path: '/maintenance',
    name: 'maintenance',
    component: () => import(/* webpackChunkName: "maintenance" */ '@/views/Maintenance.vue'),
    meta: {
      requiresAuth: false,
      layout: 'no-nav'
    }
  },
  {
    path: '/template/:templateId/:page',
    name: 'templateSetup',
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/Guide/TemplateSetup.vue'),

    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },

    props: route => ({
      templateId: route.params.templateId,
      page: Number(route.params.page)
    })
  },
  {
    path: '/sponsor-programs/:user_id',
    name: 'sponsorProgramsPage',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/Landing.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    },
    props: route => ({
      userId: route.params.user_id,
      template: route.query.template,
      pathways: route.query.pathways,
      location: route.query.location
    })
  },
  {
    path: '/set-password',
    name: 'setPassword',
    component: () => import(/* webpackChunkName: "SetPassword" */ '@/views/SetPassword.vue'),
    props: route => ({
      token: route.query.token
    })
  },
  {
    path: '/sponsor/:shareCode/:id',
    name: 'EditSponsor',
    // component: () => import(/* webpackChunkName: "Guide" */ '@/views/Explore/Guide.vue'), // ! change this explore programs!!
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/Landing.vue'), // ! change this explore programs!!
    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },
    // beforeEnter(to, from, next) {
    //   const { getUser } = useUserGetters(['getUser']);
    //   if (getUser.value) {
    //     next();
    //   } else {
    //     localStorage.setItem('sponsorShipLinkPath', to.path);
    //     next({ name: 'login' });
    //   }
    // },
    props: route => ({
      shareCode: route.params.shareCode,
      isEdit: route.params.id === 'edit'
    })
  },
  {
    path: '/temp-setup-site',
    name: 'tempSetupSite',
    component: () => import('@/views/Guide/components/SetupWizard/SetupSiteWizard.vue'),
    meta: {
      layout: 'no-nav'
    },
    props: {
      loading: false,
      errors: false
    }
  },
  {
    path: '/bulk-scope-setup',
    name: 'bulkScopeSetup',
    component: () => import('@/views/Guide/components/SetupWizard/BulkSetupScopesWizard.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/chuckie-talkie',
    name: 'chuckieTalkie',
    component: () => import('@/views/ChuckieTalkie.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      metaTags: [
        {
          name: 'mobile-web-app-capable',
          content: 'yes'
        },
        {
          name: 'apple-mobile-web-app-status-bar-style',
          content: 'black-translucent'
        },
        {
          name: 'apple-mobile-web-app-title',
          content: 'Chuckie Talkie'
        }
      ]
    }
  },
  {
    path: '/organizations',
    name: 'organizations',
    component: () => import('@/views/Organizations/OrganizationsTable.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false
    }
  },
  {
    path: '/admin-program-link',
    name: 'adminProgramLink',
    component: () => import(/* webpackChunkName: "Landing" */ '@/views/Landing.vue'),
    props: route => ({
      preSelectedTemplate: route.query.template,
      isMasterAdmin: true
    }),
    meta: {
      layout: 'no-nav'
    }
  },

  {
    path: '/pricing',
    name: 'Pricing',
    component: () => import(/* webpackChunkName: "Landing" */ '../views/Pricing.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    // L0/L1 monetization MVP plans page. Keeps legacy /pricing intact while
    // the new flow is in beta. Once stable, point /pricing here too.
    path: '/plans',
    name: 'Plans',
    component: () => import(/* webpackChunkName: "Landing" */ '../views/Plans.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/sponsor',
    name: 'SponsorForm',
    component: () => import('@/views/Sponsor.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    },
    props: route => ({
      email: route.query.email
      // example: http://localhost:8080/sponsor?email=dericklee@wehope.org
    })
  },
  {
    path: '/numerology',
    name: 'numerology',
    component: () => import('@/views/Numerology.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/guide/organizer/:programId/:page',
    name: 'guide/organizer',
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/Guide/OrganizerGuide.vue'),

    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },

    props: route => ({
      studentId: route.query.studentId,
      programId: route.params.programId,
      page: Number(route.params.page),
      monitorOrganizerView: route.query.monitorOrganizerView === 'true',
      previewView: route.query.preview === 'true'
    }),

    async beforeEnter(to, from, next) {
      clearProgramDoc();
      next();
    }
  },
  {
    path: '/program/organizer/:program_name?',
    name: 'program/organizer',
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/Guide/OrganizerSetup.vue'),

    meta: {
      layout: 'no-nav',
      requiresAuth: true,
      requiresUser: true
    },

    props: route => ({
      studentId: route.query.studentId,
      program_name: route.params.program_name,
      page: Number(route.params.page),
      monitorOrganizerView: route.query.monitorOrganizerView === 'true',
      previewView: route.query.preview === 'true',
      adk: route.query.adk,
      type: 'program'
    })
  },
  {
    path: '/sponsor-payment-mock',
    name: 'sponsor-payment-mock',
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/SponsorPayment.vue'),

    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    },

    props: route => ({
      studentId: route.query.studentId || 'mock-student-123',
      id: route.query.id || 'mock-program-456',
      amount: route.query.amount || '150'
    })
  },
  {
    path: '/sponsor-payment',
    name: 'sponsor-payment',
    component: () => import(/* webpackChunkName: "Guide" */ '@/views/SponsorPayment.vue'),

    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    },

    props: route => ({
      studentId: route.query.studentId,
      id: route.query.id,
      amount: route.query.amount
    })
  },
  {
    path: '/test-sponsor-link',
    name: 'testSponsorLink',
    component: () => import('@/views/TestSponsorLink.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/topsecret-passcode',
    name: 'secretPasscode',
    component: () => import('@/views/SecretPasscode.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/topsecret',
    name: 'secret',
    component: () => import('@/views/SecretPage.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    },
    beforeEnter(to, from, next) {
      // Check if user has access to the secret page
      const hasAccess = localStorage.getItem('secretAccess');
      const accessTime = localStorage.getItem('secretAccessTime');

      if (hasAccess && accessTime) {
        // Check if access is still valid (24 hours)
        const now = Date.now();
        const accessTimeNum = parseInt(accessTime, 10);
        const twentyFourHours = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

        if (now - accessTimeNum < twentyFourHours) {
          next();
        } else {
          // Access expired, redirect to passcode entry
          localStorage.removeItem('secretAccess');
          localStorage.removeItem('secretAccessTime');
          localStorage.removeItem('usedPhoneNumber');
          next({ name: 'secretPasscode' });
        }
      } else {
        // No access, redirect to passcode entry
        next({ name: 'secretPasscode' });
      }
    }
  },
  {
    // RETIRE EVENTUALLY - redirects to /guides/moderator
    path: '/moderator',
    redirect: '/guides/moderator'
  },
  {
    path: '/guides/moderator',
    name: 'moderatorGuide',
    component: () => import('@/views/ModeratorGuide.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/submit',
    name: 'submitAirtable',
    component: () => import('@/views/SubmitAirtable.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    // RETIRE EVENTUALLY - redirects to /guides/demo
    path: '/demo',
    redirect: '/guides/demo'
  },
  {
    path: '/guides/demo',
    name: 'demoGuide',
    component: () => import('@/views/DemoGuide.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/guides/slides',
    name: 'slidesGuide',
    component: () => import('@/views/SlidesGuide.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/guides/files',
    name: 'filesGuide',
    component: () => import('@/views/FilesGuide.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/guides/research',
    name: 'researchGuide',
    component: () => import('@/views/ResearchGuide.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/guides/train',
    name: 'trainGuide',
    component: () => import('@/views/TrainGuide.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/test-activity/train',
    name: 'testActivityTrain',
    component: () => import('@/views/TestActivityTrain.vue'),
    meta: {
      layout: 'default',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/guides/forum',
    name: 'forumGuide',
    component: () => import('@/views/ForumGuide.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/test-activity/forum',
    name: 'testActivityForum',
    component: () => import('@/views/TestActivityForum.vue'),
    meta: {
      layout: 'default',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/guides/ideate',
    name: 'ideateGuide',
    component: () => import('@/views/IdeateGuide.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/test-activity/ideate',
    name: 'testActivityIdeate',
    component: () => import('@/views/TestActivityIdeate.vue'),
    meta: {
      layout: 'default',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/guides/pitch',
    name: 'pitchGuide',
    component: () => import('@/views/PitchGuide.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/test-activity/pitch',
    name: 'testActivityPitch',
    component: () => import('@/views/TestActivityPitch.vue'),
    meta: {
      layout: 'default',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/guides/make',
    name: 'makeGuide',
    component: () => import('@/views/MakeGuide.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/test-activity/make',
    name: 'testActivityMake',
    component: () => import('@/views/TestActivityMake.vue'),
    meta: {
      layout: 'default',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/guides/achievements',
    name: 'achievementsGuide',
    component: () => import('@/views/AchievementsGuide.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/guides/credentials',
    name: 'credentialsGuide',
    component: () => import('@/views/AchievementsGuide.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/guides/promotion',
    name: 'invitationsGuide',
    component: () => import('@/views/InvitationsGuide.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    // RETIRE EVENTUALLY - redirects to /guides/watchparty
    path: '/watchpartyguide',
    redirect: '/guides/watchparty'
  },
  {
    path: '/guides/watchparty',
    name: 'watchPartyGuide',
    component: () => import('@/views/WatchPartyGuide.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    // RETIRE EVENTUALLY - redirects to /guides/production
    path: '/production',
    redirect: '/guides/production'
  },
  {
    path: '/guides/production',
    name: 'productionRundownGuide',
    component: () => import('@/views/ProductionRundownGuide.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    // RETIRE EVENTUALLY - redirects to /guides/bayfairtour
    path: '/bayfairtour',
    redirect: '/guides/bayfairtour'
  },
  {
    path: '/guides/bayfairtour',
    name: 'bayfairEscapeRoomTourGuide',
    component: () => import('@/views/BayfairEscapeRoomTourGuide.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    // RETIRE EVENTUALLY - redirects to /guides/testimonial
    path: '/testimonial',
    redirect: '/guides/testimonial'
  },
  {
    path: '/guides/testimonial',
    name: 'testimonialInterviewGuide',
    component: () => import('@/views/TestimonialInterviewGuide.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    // RETIRE EVENTUALLY - redirects to /guides/employerconversion
    path: '/employerconversion',
    redirect: '/guides/employerconversion'
  },
  {
    path: '/guides/employerconversion',
    name: 'employerConversionGuide',
    component: () => import('@/views/EmployerConversionGuide.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/discover',
    name: 'discover',
    component: () => import('@/views/Discover.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/discover/:participant',
    name: 'discoverParticipant',
    component: () => import('@/views/Discover.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  // Sponsor checkout return routes - handles Stripe hosted checkout callbacks
  {
    path: '/discover/sponsor-success',
    name: 'sponsorSuccess',
    component: () => import('@/views/Discover/SponsorSuccess.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/discover/sponsor-cancel',
    name: 'sponsorCancel',
    component: () => import('@/views/Discover/SponsorCancel.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/finale',
    name: 'finale',
    beforeEnter() {
      window.location.href =
        'https://docs.google.com/document/d/1Pq3RlJMQVEwP1NbyaRTXqPkHTzMAX1e5vDuec_VOka8/edit?usp=sharing';
    }
  },
  {
    path: '/mypremiere',
    name: 'myPremiere',
    component: () => import('@/views/MyPremiere.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/k12swp',
    name: 'k12swp',
    component: () => import('@/views/K12SWP.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity K12 SWP Grant Writer',
      description:
        "Help schools win more CTE & SWP funding with PilotCity's Grant Writing support.",
      image: 'https://pilotcity.com/assets/k12swp-preview.png',
      metaTags: [
        {
          name: 'description',
          content: "Help schools win more CTE & SWP funding with PilotCity's Grant Writing support."
        },
        {
          name: 'keywords',
          content: 'k12, swp, cte, grant writing, pilotcity, education funding'
        },
        {
          name: 'author',
          content: 'PilotCity'
        },
        {
          property: 'og:title',
          content: 'PilotCity K12 SWP Grant Writer'
        },
        {
          property: 'og:description',
          content: "Help schools win more CTE & SWP funding with PilotCity's Grant Writing support."
        },
        {
          property: 'og:type',
          content: 'website'
        },
        {
          property: 'og:url',
          content: 'https://pilotcity.com/k12swp'
        },
        {
          property: 'og:image',
          content: 'https://pilotcity.com/assets/k12swp-preview.png'
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image'
        },
        {
          name: 'twitter:title',
          content: 'PilotCity K12 SWP Grant Writer'
        },
        {
          name: 'twitter:description',
          content: "Help schools win more CTE & SWP funding with PilotCity's Grant Writing support."
        },
        {
          name: 'twitter:image',
          content: 'https://pilotcity.com/assets/k12swp-preview.png'
        }
      ]
    }
  },
  {
    path: '/mysa',
    name: 'mysa',
    component: () => import('@/views/MYSA.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: "MYSA - Women's Health & Wellness Platform",
      description:
        "Gamified, AI-powered platform for teen girls' health, wellness, and empowerment.",
      metaTags: [
        {
          name: 'description',
          content:
            'MYSA is a gamified, AI-powered platform that meets girls where they are with period care, mental wellness, and cultural community.'
        },
        {
          name: 'keywords',
          content:
            'womens health, teen wellness, period care, mental health, gamification, AI, subscription boxes'
        },
        {
          name: 'author',
          content: 'MYSA'
        },
        {
          property: 'og:title',
          content: "MYSA - Women's Health & Wellness Platform"
        },
        {
          property: 'og:description',
          content:
            "Gamified, AI-powered platform for teen girls' health, wellness, and empowerment."
        },
        {
          property: 'og:type',
          content: 'website'
        },
        {
          property: 'og:url',
          content: 'https://pilotcity.com/mysa'
        },
        {
          name: 'viewport',
          content: 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
        }
      ]
    }
  },
  {
    path: '/pitch1',
    name: 'pitch1',
    component: () => import(/* webpackChunkName: "Pitch1" */ '@/views/Pitch1.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity Pitch Deck — The AI Operating System for Programs',
      description:
        'PilotCity is the AI platform that creates, runs, and measures programs for any organization — from schools to cities.',
      metaTags: [
        {
          name: 'description',
          content:
            'PilotCity is the AI platform that creates, runs, and measures programs for any organization — from schools to cities.'
        },
        {
          property: 'og:title',
          content: 'PilotCity — The AI Operating System for Programs'
        },
        {
          property: 'og:description',
          content:
            'Investor deck: PilotCity builds, runs, and measures programs automatically using AI.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-pitch.jpg'
        },
        {
          name: 'theme-color',
          content: '#00C897'
        }
      ]
    }
  },
  {
    path: '/home5',
    name: 'home5',
    component: () => import(/* webpackChunkName: "Home5" */ '@/views/Home5.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity Protocol — The Sponsorship Layer for Human Advancement',
      description:
        'Fund anyone, anywhere — to complete meaningful programs that build careers, communities, and cities. PilotCity turns sponsorships into tokens and impact into proof.',
      metaTags: [
        {
          name: 'description',
          content:
            'Fund anyone, anywhere — to complete meaningful programs that build careers, communities, and cities. PilotCity turns sponsorships into tokens and impact into proof.'
        },
        {
          property: 'og:title',
          content: 'PilotCity Protocol — The Sponsorship Layer for Human Advancement'
        },
        {
          property: 'og:description',
          content:
            'Sponsorships become tokens. Impact becomes measurable. The future of progress is programmable.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-sponsorship.jpg'
        },
        {
          name: 'theme-color',
          content: '#00C897'
        }
      ]
    }
  },
  {
    path: '/home6',
    name: 'home6',
    component: () => import(/* webpackChunkName: "Home6v2" */ '@/views/Home6v2.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity - The Platform for Building the Future',
      description:
        'Create, fund, and scale impactful programs with AI-powered tools, transparent tokenomics, and real-time analytics. The operating system for program creators.',
      metaTags: [
        {
          name: 'description',
          content:
            'Create, fund, and scale impactful programs with AI-powered tools, transparent tokenomics, and real-time analytics.'
        },
        {
          property: 'og:title',
          content: 'PilotCity - The Platform for Building the Future'
        },
        {
          property: 'og:description',
          content:
            'AI-powered program creation, transparent tokenomics, and real-time analytics. Build programs that matter.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-home.jpg'
        },
        {
          name: 'theme-color',
          content: '#06B6D4'
        }
      ]
    }
  },
  {
    path: '/home6-v1',
    name: 'home6v1',
    component: () => import(/* webpackChunkName: "Home6" */ '@/views/Home6.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  {
    path: '/chambers',
    name: 'chambers',
    component: () => import(/* webpackChunkName: "Chambers" */ '@/views/Chambers.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity for Chambers of Commerce — Empowering Local Workforce Development',
      description:
        'PilotCity helps Chambers of Commerce create, manage, and monetize workforce development programs. Empower your community with impactful programs today!',
      metaTags: [
        {
          name: 'description',
          content:
            'PilotCity helps Chambers of Commerce create, manage, and monetize workforce development programs. Empower your community with impactful programs today!'
        },
        {
          property: 'og:title',
          content: 'PilotCity for Chambers of Commerce'
        },
        {
          property: 'og:description',
          content:
            'Empower your Chamber with automated workforce programs, sponsor-driven opportunities, and revenue generation. Start creating impactful programs today.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-chambers.jpg'
        },
        {
          name: 'theme-color',
          content: '#00C897'
        }
      ]
    }
  },
  {
    path: '/chambers2',
    name: 'chambers2',
    component: () => import(/* webpackChunkName: "Chambers2" */ '@/views/Chambers2.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity - Empowering Chambers to Create Workforce Development Programs',
      description:
        'PilotCity enables Chambers of Commerce to create, manage, and monetize internship programs for local businesses, students, and sponsors. Start creating today.',
      metaTags: [
        {
          name: 'description',
          content:
            'PilotCity enables Chambers of Commerce to create, manage, and monetize internship programs for local businesses, students, and sponsors. Start creating today.'
        },
        {
          property: 'og:title',
          content: 'PilotCity - Empowering Chambers to Create Workforce Development Programs'
        },
        {
          property: 'og:description',
          content:
            'PilotCity transforms Chambers into program creators, helping them connect local businesses with students and generate new revenue.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-chambers.jpg'
        },
        {
          name: 'theme-color',
          content: '#00C897'
        }
      ]
    }
  },
  {
    path: '/city-landing',
    name: 'cityLanding',
    component: () => import(/* webpackChunkName: "CityLanding" */ '@/views/CityLanding.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity - Empowering Cities to Create Workforce Development Programs',
      description:
        'PilotCity helps cities create scalable, revenue-generating programs that engage residents, businesses, and schools while achieving local government goals.',
      metaTags: [
        {
          name: 'description',
          content:
            'PilotCity helps cities create scalable, revenue-generating programs that engage residents, businesses, and schools while achieving local government goals.'
        },
        {
          property: 'og:title',
          content: 'PilotCity - Empowering Cities to Create Workforce Development Programs'
        },
        {
          property: 'og:description',
          content:
            'PilotCity transforms city governments into workforce development leaders, allowing them to create impactful programs for their communities.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-city.jpg'
        },
        {
          name: 'theme-color',
          content: '#4B89A4'
        }
      ]
    }
  },
  {
    path: '/cte-teacher-landing',
    name: 'cteTeacherLanding',
    component: () =>
      import(/* webpackChunkName: "CTETeacherLanding2" */ '@/views/CTETeacherLanding2.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity - Real-World Work Experiences for Every Student',
      description:
        'Connect your CTE students with internships, industry projects, and employers. Build portfolios, résumés, and college applications—all in one platform.',
      metaTags: [
        {
          name: 'description',
          content:
            'Connect your CTE students with internships, industry projects, and employers. Build portfolios, résumés, and college applications—all in one platform.'
        },
        {
          property: 'og:title',
          content: 'PilotCity - Real-World Work Experiences for Every Student'
        },
        {
          property: 'og:description',
          content:
            'Give your CTE students the competitive edge with real work experiences, industry projects, and employer connections.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-cte.jpg'
        },
        {
          name: 'theme-color',
          content: '#00C897'
        }
      ]
    }
  },
  {
    path: '/cte-teacher-landing-v1',
    name: 'cteTeacherLandingV1',
    component: () =>
      import(/* webpackChunkName: "CTETeacherLanding" */ '@/views/CTETeacherLanding.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  // Retired 2026-10-05: claimed SOC 2, FERPA and Perkins V compliance, verified credentials and
  // automated grant reporting, none of them proven (Master Narrative fix 3).
  { path: '/cte-school-admin-landing', redirect: '/' },
  { path: '/cte-school-admin-landing-v1', redirect: '/' },
  {
    path: '/employers-landing',
    name: 'employersLanding',
    component: () =>
      import(/* webpackChunkName: "EmployersLanding2" */ '@/views/EmployersLanding2.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false,
      title: 'PilotCity - Build Your Talent Pipeline | Internship Platform for Employers',
      description:
        'Connect with motivated students. Create internship programs in minutes. Build your talent pipeline with PilotCity—zero setup fees, launch in 15 minutes.',
      metaTags: [
        {
          name: 'description',
          content:
            'Connect with motivated students. Create internship programs in minutes. Build your talent pipeline with PilotCity—zero setup fees, launch in 15 minutes.'
        },
        {
          property: 'og:title',
          content: 'PilotCity - Build Your Talent Pipeline'
        },
        {
          property: 'og:description',
          content:
            'The premier platform for employers to create internship programs, connect with students, and build diverse talent pipelines. Zero setup fees.'
        },
        {
          property: 'og:image',
          content: '/og-pilotcity-employers.jpg'
        },
        {
          name: 'theme-color',
          content: '#3B48F2'
        }
      ]
    }
  },
  {
    path: '/employers-landing-v1',
    name: 'employersLandingV1',
    component: () =>
      import(/* webpackChunkName: "EmployersLanding" */ '@/views/EmployersLanding.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  // ============================================
  // HEALTH DASHBOARD ROUTES
  // Personal health data visualization - Lee Family
  // ============================================
  {
    path: '/health/dericklee',
    name: 'healthDashboardDerickLee',
    component: () => import('@/views/Health/DerickLee.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    }
  },
  // ============================================================
  // ============================================================
  // PRA EVIDENCE DESK (public-records document review)
  // Self-contained tool with its OWN session auth (x-pra-session header,
  // NOT Auth0). No requiresAuth — the view gates itself behind PraLogin.
  // MUST be declared before the /:slug wildcard or it gets captured as a slug.
  // ============================================================
  {
    path: '/pra',
    name: 'praReview',
    component: () => import(/* webpackChunkName: "PraReview" */ '@/views/PraReview/PraReview.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  {
    path: '/pra/brief',
    name: 'praBrief',
    component: () => import(/* webpackChunkName: "PraReview" */ '@/views/PraReview/BriefView.vue'),
    meta: {
      layout: 'no-nav'
    }
  },
  // ============================================================
  // PUBLIC PORTFOLIO SLUG ROUTES (WILDCARD - MUST BE NEAR END)
  // ============================================================
  // These routes use dynamic segments and must be defined AFTER all
  // specific routes to ensure specific paths match first.
  // Direct URL access: pilotcity.com/:slug (user or organization)
  {
    path: '/:slug',
    name: 'PublicProfileRoot',
    component: () => import(/* webpackChunkName: "Portfolio" */ '@/views/Portfolio/Portfolio.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    },
    props: route => ({
      slug: route.params.slug,
      isPublicProfile: true
    }),
    beforeEnter(to, from, next) {
      const slug = ((to.params.slug as string) || '').toLowerCase();

      // Reserved paths - let other routes handle or show 404
      const RESERVED_SLUGS = [
        'sponsor',
        'portfolio',
        'wallet',
        'program',
        'guide',
        'activity',
        'mentor',
        'match',
        'discover',
        'monitor',
        'login',
        'signup',
        'invite',
        'health',
        'employer',
        'teacher',
        'join',
        'api',
        'admin',
        'settings',
        'profile',
        'public',
        'private',
        'user',
        'org',
        'organization',
        'help',
        'support',
        'about',
        'contact',
        'terms',
        'privacy',
        'legal',
        'blog',
        'docs',
        'documentation',
        'faq',
        'payment-wallet',
        'profile-setting',
        'authRequired',
        'maintenance',
        'template',
        'why-join',
        'directory',
        'summeronboard',
        'civic-seal',
        'nysed',
        'civics',
        'new-york',
        '404'
      ];

      if (RESERVED_SLUGS.includes(slug)) {
        // Reserved path - let it fall through to 404
        next({ name: '404' });
        return;
      }

      // Valid slug - proceed to public profile
      next();
    }
  },
  // Slug with component path: pilotcity.com/:slug/:component
  {
    path: '/:slug/:component',
    name: 'PublicProfileComponent',
    component: () => import(/* webpackChunkName: "Portfolio" */ '@/views/Portfolio/Portfolio.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    },
    props: route => ({
      slug: route.params.slug,
      component: route.params.component,
      isPublicProfile: true
    }),
    beforeEnter(to, from, next) {
      const slug = ((to.params.slug as string) || '').toLowerCase();

      // Reserved paths - let other routes handle or show 404
      const RESERVED_SLUGS = [
        'sponsor',
        'portfolio',
        'wallet',
        'program',
        'guide',
        'activity',
        'mentor',
        'match',
        'discover',
        'monitor',
        'login',
        'signup',
        'invite',
        'health',
        'employer',
        'teacher',
        'join',
        'api',
        'admin',
        'settings',
        'profile',
        'public',
        'private',
        'user',
        'org',
        'organization',
        'help',
        'support',
        'about',
        'contact',
        'terms',
        'privacy',
        'legal',
        'blog',
        'docs',
        'documentation',
        'faq',
        'payment-wallet',
        'profile-setting',
        'authRequired',
        'maintenance',
        'template',
        'why-join',
        'directory',
        'summeronboard',
        'civic-seal',
        'nysed',
        'civics',
        'new-york',
        '404'
      ];

      if (RESERVED_SLUGS.includes(slug)) {
        // Reserved path - let it fall through to 404
        next({ name: '404' });
        return;
      }

      // Valid slug - proceed to public profile
      next();
    }
  },
  // Fallback user profile route: pilotcity.com/u/:slug
  {
    path: '/u/:slug/:component?',
    name: 'UserProfileFallback',
    component: () => import(/* webpackChunkName: "Portfolio" */ '@/views/Portfolio/Portfolio.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    },
    props: route => ({
      slug: route.params.slug,
      component: route.params.component,
      isPublicProfile: true,
      forceType: 'user'
    })
  },
  // Fallback organization profile route: pilotcity.com/o/:slug
  {
    path: '/o/:slug/:component?',
    name: 'OrgProfileFallback',
    component: () => import(/* webpackChunkName: "Portfolio" */ '@/views/Portfolio/Portfolio.vue'),
    meta: {
      layout: 'no-nav',
      requiresAuth: false,
      requiresUser: false
    },
    props: route => ({
      slug: route.params.slug,
      component: route.params.component,
      isPublicProfile: true,
      forceType: 'organization'
    })
  },
  // ============================================================
  // END PUBLIC PORTFOLIO SLUG ROUTES
  // ============================================================
  // ============================================================
  // CATCH-ALL 404 ROUTE (MUST BE LAST)
  // ============================================================
  {
    path: '*',
    name: '404',
    component: () => import(/* webpackChunkName: "404" */ '@/views/Error404.vue')
  }
];

export default routes;
